import {
    applyTableFilters,
    applyTableSort
} from '../byor/utils/tableFilterUtils';
import React, { useMemo } from 'react';
import { DataTableWidget } from './DataTableWidget';
import { getLabelStyle } from './widgetLabelHelpers';
import './FindingsWidgets.css';

// Helper function to calculate pagination slice indices
// Supports different itemsPerPage for first page vs continuation pages
const getPaginationSlice = (pagination) => {
  if (!pagination) return null;
  const startIndex = pagination.startIndex !== undefined 
    ? pagination.startIndex 
    : pagination.currentPage * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  return { startIndex, endIndex };
};

/**
 * Helper to apply hiddenColumns from config to show* booleans.
 * Takes config and a mapping of { columnHeaderName: showKey }.
 * Returns an object of overridden show* values.
 */

export const getDisplayHeader = (config, headerName) => {
  return config?.customColumnHeaders?.[headerName] || headerName;
};

const applyHiddenColumns = (config, columnMap) => {
  const hiddenColumns = config?.hiddenColumns || [];
  if (hiddenColumns.length === 0) return {};
  const overrides = {};
  for (const [colName, showKey] of Object.entries(columnMap)) {
    if (hiddenColumns.includes(colName)) {
      overrides[showKey] = false;
    }
  }
  return overrides;
};

/**
 * =============================================================================
 * PROOF TABLES WIDGETS - PART 2
 * =============================================================================
 *
 * Continuation of proof tables:
 * - Localhost (Localhost.js): 32 tables
 * - Microsoft 365 (MS.js): 6 tables
 * - HIBP/Dark Web (HIBP.js): 4 tables
 * - PII (PII.js): 3 tables
 */

// =============================================================================
// LOCALHOST SCAN PROOF TABLES (32 widgets)
// =============================================================================

/**
 * Localhost - All Users Password Status Table
 */
/**
 * Localhost - All Users Password Status Table
 */
export const LocalhostAllUsersPasswordTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Username': 'showUsername', 'Computer': 'showComputer', 'Account Type': 'showAccountType', 'Password Required': 'showPasswordRequired', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'Localhost - All Users Password Status',
    showNo = true,
    showUsername = true,
    showComputer = true,
    showAccountType = true,
    showPasswordRequired = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-all-users-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allUsersData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hardware = localhost['Hardware Informations'] || {};
    const users = hardware.users || [];

    const rawData = users.map((user, index) => ({
      no: index + 1,
      username: user.Name || user.Username || '',
      computer: 'Localhost',
      account_type: user.AccountType || user.Type || 'Standard',
      password_required: user.PasswordRequired ? 'Yes' : 'No',
      password_expires: user.PasswordExpires ? 'Yes' : 'No'
    }));

    return applyTableSort(
      applyTableFilters(rawData, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayUsers = useMemo(() => {
    if (!pagination) return allUsersData;
    const slice = getPaginationSlice(pagination);
    return allUsersData.slice(slice.startIndex, slice.endIndex);
  }, [allUsersData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showUsername, showComputer, showAccountType, showPasswordRequired, showPasswordExpires].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1.5fr 1fr 1.5fr 1fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showComputer && <div style={headerStyle}>{getDisplayHeader(config, 'Computer')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordRequired && <div style={headerStyle}>{getDisplayHeader(config, 'Password Required')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showUsername && <div style={rowStyleMerged}>{user.username}</div>}
                    {showComputer && <div style={rowStyleMerged}>{user.computer}</div>}
                    {showAccountType && <div style={rowStyleMerged}>{user.account_type}</div>}
                    {showPasswordRequired && <div style={rowStyleMerged}>{user.password_required}</div>}
                    {showPasswordExpires && <div style={rowStyleMerged}>{user.password_expires}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Localhost - Users With Non-Expiring Passwords Table
 */
/**
 * Localhost - Users With Non-Expiring Passwords Table
 */
export const LocalhostNonExpiringPasswordsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Computer IP': 'showComputerIP', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Required': 'showPasswordRequired' });
  const {
    title = 'Localhost - Users With Non-Expiring Passwords',
    showNo = true,
    showComputerIP = true,
    showUsername = true,
    showAccountType = true,
    showPasswordRequired = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-non-expiring-passwords-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allUsersData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hardware = localhost['Hardware Informations'] || {};
    const users = hardware.users || [];

    const rawData = users
      .filter(user => !user.PasswordExpires)
      .map((user, index) => ({
        no: index + 1,
        computer_ip: 'Localhost',
        username: user.Name || user.Username || '',
        account_type: user.AccountType || user.Type || 'Standard',
        password_required: user.PasswordRequired ? 'Yes' : 'No'
      }));

    return applyTableSort(
      applyTableFilters(rawData, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayUsers = useMemo(() => {
    if (!pagination) return allUsersData;
    const slice = getPaginationSlice(pagination);
    return allUsersData.slice(slice.startIndex, slice.endIndex);
  }, [allUsersData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showUsername, showAccountType, showPasswordRequired].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1fr 2fr 1.5fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'Computer IP')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordRequired && <div style={headerStyle}>{getDisplayHeader(config, 'Password Required')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users with non-expiring passwords found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div style={rowStyleMerged}>{user.computer_ip}</div>}
                    {showUsername && <div style={rowStyleMerged}>{user.username}</div>}
                    {showAccountType && <div style={rowStyleMerged}>{user.account_type}</div>}
                    {showPasswordRequired && <div style={rowStyleMerged}>{user.password_required}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Localhost - Disk Usage Table
 */
/**
 * Localhost - Disk Usage Table
 */
export const LocalhostDiskUsageTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Device': 'showDevice', 'Drive Letter': 'showDriveLetter', 'Disk Usage': 'showDiskUsage', 'Used (GB)': 'showUsedGB', 'Total (GB)': 'showTotalGB', 'Status': 'showStatus' });
  const {
    title = 'Localhost - Disk Usage',
    showNo = true,
    showDevice = true,
    showDriveLetter = true,
    showDiskUsage = true,
    showUsedGB = true,
    showTotalGB = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-disk-usage-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allDiskData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hardware = localhost['Hardware Informations'] || {};
    const disks = hardware.disks || [];

    const rawData = disks.map((disk, index) => {
      const usedSpace = disk.UsedSpace || disk.used || 0;
      const totalSpace = disk.TotalSpace || disk.total || 1;
      const usagePercent = Math.round((usedSpace / totalSpace) * 100);

      let status = 'Good';
      if (usagePercent >= 95) status = 'Critical';
      else if (usagePercent >= 85) status = 'Warning';

      return {
        no: index + 1,
        device: 'Localhost',
        drive_letter: disk.DriveLetter || disk.drive || '',
        disk_usage: `${usagePercent}%`,
        used_gb: Math.round(usedSpace / (1024 ** 3)),
        total_gb: Math.round(totalSpace / (1024 ** 3)),
        status
      };
    });

    return applyTableSort(
      applyTableFilters(rawData, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayDiskRows = useMemo(() => {
    if (!pagination) return allDiskData;
    const slice = getPaginationSlice(pagination);
    return allDiskData.slice(slice.startIndex, slice.endIndex);
  }, [allDiskData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showDevice, showDriveLetter, showDiskUsage, showUsedGB, showTotalGB, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 7 
    ? '0.5fr 1fr 1fr 1fr 1fr 1fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showDriveLetter && <div style={headerStyle}>{getDisplayHeader(config, 'Drive Letter')}</div>}
            {showDiskUsage && <div style={headerStyle}>{getDisplayHeader(config, 'Disk Usage')}</div>}
            {showUsedGB && <div style={headerStyle}>{getDisplayHeader(config, 'Used (GB)')}</div>}
            {showTotalGB && <div style={headerStyle}>{getDisplayHeader(config, 'Total (GB)')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayDiskRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No disk usage data available.</p>
              </div>
            ) : (
              displayDiskRows.map((disk, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;
                
                const statusColors = {
                  'Critical': theme?.statusColors?.red || '#d71920',
                  'Warning': theme?.statusColors?.yellow || '#fdbc13',
                  'Good': theme?.statusColors?.green || '#6fca42'
                };
                const statusColor = statusColors[disk.status] || '#666';

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDevice && <div style={rowStyleMerged}>{disk.device}</div>}
                    {showDriveLetter && <div style={rowStyleMerged}>{disk.drive_letter}</div>}
                    {showDiskUsage && <div style={rowStyleMerged}>{disk.disk_usage}</div>}
                    {showUsedGB && <div style={rowStyleMerged}>{disk.used_gb}</div>}
                    {showTotalGB && <div style={rowStyleMerged}>{disk.total_gb}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {disk.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Firewall Status Data
 * Matches backend logic from template2/Localhost.js:621-659
 */
export const extractLocalhostFirewallStatusData = (data) => {
  try {
    // Extract from raw scan data - match backend _Localhost_Firewall_status logic
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
    
    const rows = [];
    const name = 'localhost (127.0.0.1)';
    const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

    const firewall = localhost?.["System"]?.Firewall || localhost?.Firewall;

    if (!firewall || typeof firewall !== 'object') {
      rows.push({
        device: name,
        hostname: hostname,
        profiles: '-',
        status: 'Disabled'
      });
    } else {
      const domain = firewall.Domain === true;
      const privateNet = firewall.Private === true;
      const publicNet = firewall.Public === true;
      
      const profileSummary = `Domain: ${domain ? 'Enabled' : 'Disabled'}, Private: ${privateNet ? 'Enabled' : 'Disabled'}, Public: ${publicNet ? 'Enabled' : 'Disabled'}`;
      
      const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;

      let status = 'Disabled';
      if (enabledCount === 3) {
        status = 'Fully Enabled';
      } else if (enabledCount > 0) {
        status = 'Partially Enabled';
      }

      rows.push({
        device: name,
        hostname: hostname,
        profiles: profileSummary,
        status: status
      });
    }

    return rows;
  } catch (error) {
    console.log('Error extractLocalhostFirewallStatusData', error);
    return [];
  }
};

/**
 * Localhost - Firewall Status Table
 * Displays localhost firewall status matching backend template2/Localhost.js:531-543
 * Widget auto-sizes to fit all content
 */
/**
 * Localhost - Firewall Status Table
 * Displays localhost firewall status matching backend template2/Localhost.js:531-543
 */
export const LocalhostFirewallStatusTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'IP Address': 'showDevice', 'Hostname': 'showHostname', 'Firewall Profiles': 'showProfiles', 'Status': 'showStatus' });
  const {
    title = 'Localhost Firewall Status',
    showDevice = true,
    showHostname = true,
    showProfiles = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-firewall-status-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allFirewallRows = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostFirewallStatusData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const firewallRows = useMemo(() => {
    if (!pagination) return allFirewallRows;
    const slice = getPaginationSlice(pagination);
    return allFirewallRows.slice(slice.startIndex, slice.endIndex);
  }, [allFirewallRows, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showDevice, showHostname, showProfiles, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1.2fr 1.2fr 2.5fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showProfiles && <div style={headerStyle}>{getDisplayHeader(config, 'Firewall Profiles')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {firewallRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No firewall data available.</p>
              </div>
            ) : (
              firewallRows.map((row, idx) => {
                const statusColors = {
                  'Fully Enabled': theme?.statusColors?.green || '#6fca42',
                  'Partially Enabled': theme?.statusColors?.yellow || '#fdbc13',
                  'Disabled': theme?.statusColors?.red || '#d71920'
                };
                const statusColor = statusColors[row.status] || '#666';

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showDevice && <div style={rowStyleMerged}>{row.device}</div>}
                    {showHostname && <div style={rowStyleMerged}>{row.hostname}</div>}
                    {showProfiles && <div style={rowStyleMerged}>{row.profiles}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {row.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost OS Version Data
 * Matches backend logic from template2/Localhost.js:938-973
 */
export const extractLocalhostOSVersionData = (data, config = {}) => {
  try {
    const lookoutField = config.lookoutField || 'Host';
    const isAggregated = lookoutField === 'OS Name';

    // Extract from raw scan data - match backend _Localhost_OS_status logic
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
    
    const rows = [];

    // Backend lines 946-948: const osName = localhostData?.["Hardware Informations"]?.["OS"] || ""; const osVersion = localhostData?.["Hardware Informations"]?.["OS Version"] || ""; const fullOS = `${osName} ${osVersion}`;
    const hardware = localhost?.["Hardware Informations"] || {};
    const osName = hardware?.["OS"] || hardware?.OS || "";
    const osVersion = hardware?.["OS Version"] || hardware?.["OS_Version"] || hardware?.OSVersion || "";
    const fullOS = `${osName} ${osVersion}`.trim();

    if (!fullOS) {
      return rows;
    }

    // Backend lines 950-963: Determine OS_Risk based on OS pattern matching
    let OS_Risk = 'Unknown';

    if (/Windows XP|Vista|7|8(?!\.1)|2003|2008|2012/i.test(fullOS)) {
      OS_Risk = 'Critical';
    } else if (/Windows 10|2016|2019/i.test(fullOS)) {
      OS_Risk = 'Moderate';
    } else if (/Windows 11|2022/i.test(fullOS)) {
      OS_Risk = 'Normal';
    }

    if (isAggregated) {
      rows.push({
        osName: fullOS,
        count: 1,
        host: 'localhost',
        status: OS_Risk
      });
    } else {
      rows.push({
        host: 'localhost',
        osName: fullOS,
        status: OS_Risk
      });
    }

    return rows;
  } catch (error) {
    console.log('Error extractLocalhostOSVersionData', error);
    return [];
  }
};

/**
 * Localhost - OS Version Table
 * Displays localhost OS version information matching backend template2/Localhost.js:991-1003
 * Widget auto-sizes to fit all content
 */
/**
 * Localhost - OS Version Table
 * Displays localhost OS version information matching backend template2/Localhost.js:991-1003
 */
export const LocalhostOSVersionTableWidget = ({ data, config = {}, style = {}, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Host': 'showHost', 'OS Name': 'showOSName', 'Count': 'showCount', 'Status': 'showStatus' });
  const {
    title = 'Localhost OS Version',
    showHost = true,
    showOSName = true,
    showCount = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const lookoutField = restConfig.lookoutField || 'Host';
  const isAggregated = lookoutField === 'OS Name';

  const widgetType = 'localhost-os-version-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allOSData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostOSVersionData(data, restConfig), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort, restConfig]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const templateColumns = isAggregated ? '1.5fr 0.8fr 1fr 1fr' : '1fr 2fr 1fr';

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {isAggregated ? (
              <>
                {showOSName && <div style={headerStyle}>{getDisplayHeader(config, 'OS Name')}</div>}
                {showCount && <div style={headerStyle}>{getDisplayHeader(config, 'Count')}</div>}
                {showHost && <div style={headerStyle}>{getDisplayHeader(config, 'Host')}</div>}
                {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
              </>
            ) : (
              <>
                {showHost && <div style={headerStyle}>{getDisplayHeader(config, 'Host')}</div>}
                {showOSName && <div style={headerStyle}>{getDisplayHeader(config, 'OS Name')}</div>}
                {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
              </>
            )}
          </div>
          <div className="open-ports-body">
            {allOSData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No OS version data available.</p>
              </div>
            ) : (
              allOSData.map((row, idx) => {
                const statusColors = {
                  'Critical': theme?.statusColors?.red || '#d71920',
                  'Moderate': theme?.statusColors?.yellow || '#fdbc13',
                  'Normal': theme?.statusColors?.green || '#6fca42'
                };
                const statusColor = statusColors[row.status] || '#666';

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {isAggregated ? (
                      <>
                        {showOSName && <div style={rowStyleMerged}>{row.osName}</div>}
                        {showCount && <div style={rowStyleMerged}>{row.count}</div>}
                        {showHost && <div style={rowStyleMerged}>{row.host}</div>}
                      </>
                    ) : (
                      <>
                        {showHost && <div style={rowStyleMerged}>{row.host}</div>}
                        {showOSName && <div style={rowStyleMerged}>{row.osName}</div>}
                      </>
                    )}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {row.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Sensitive Open Ports Data
 * Matches backend logic from template2/Localhost.js:1254-1285
 */
export const extractLocalhostSensitiveOpenPortsData = (data) => {
  try {
    // Extract from raw scan data - match backend _Localhost_openPort_status logic
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
    
    const rows = [];

    // Backend lines 1257-1261: Define sensitive ports
    const sensitive_ports = {
      21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP",
      53: "DNS", 80: "HTTP", 139: "NetBIOS", 143: "SMB",
      3306: "MySQL", 3389: "RDP", 8080: "HTTP"
    };

    // Backend line 1264: const openPorts = localhostData?.["External vulnerability"]?.["open_ports"] || {};
    const openPorts = localhost?.["External vulnerability"]?.["open_ports"] || 
                      localhost?.["External Vulnerability"]?.["open_ports"] ||
                      localhost?.["external_vulnerability"]?.["open_ports"] ||
                      {};

    if (!openPorts || typeof openPorts !== 'object') {
      return rows;
    }

    const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

    // Backend lines 1265-1276: Iterate through IPs and ports
    const ipList = Object.keys(openPorts);

    for (const ip of ipList) {
      const ports = openPorts[ip];
      if (!ports || typeof ports !== 'object') continue;

      for (const portKey of Object.keys(ports)) {
        const portNum = parseInt(portKey);
        
        // Backend lines 1271-1273: if (sensetive_ports[portNum]) { rows.push([ip, portNum, sensetive_ports[portNum]]); }
        if (sensitive_ports[portNum]) {
          rows.push({
            ip: ip,
            hostname: hostname,
            port: portNum,
            service: sensitive_ports[portNum]
          });
        }
      }
    }

    return rows;
  } catch (error) {
    console.log('Error extractLocalhostSensitiveOpenPortsData', error);
    return [];
  }
};

/**
 * Localhost - Sensitive Open Ports Table
 * Displays sensitive open ports matching backend template2/Localhost.js:1305-1317
 * Widget auto-sizes to fit all content
 */
/**
 * Localhost - Sensitive Open Ports Table
 * Displays sensitive open ports matching backend template2/Localhost.js:1305-1317
 */
export const LocalhostSensitiveOpenPortsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Port': 'showPort', 'Service': 'showService' });
  const {
    title = 'Sensitive Open Ports - Localhost',
    showNo = true,
    showIP = true,
    showHostname = true,
    showPort = true,
    showService = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-sensitive-open-ports-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostSensitiveOpenPortsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const portRows = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showPort, showService].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1fr 1fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showPort && <div style={headerStyle}>{getDisplayHeader(config, 'Port')}</div>}
            {showService && <div style={headerStyle}>{getDisplayHeader(config, 'Service')}</div>}
          </div>
          <div className="open-ports-body">
            {portRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No sensitive open ports detected on localhost.</p>
              </div>
            ) : (
              portRows.map((row, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{row.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{row.hostname}</div>}
                    {showPort && <div style={rowStyleMerged}>{row.port}</div>}
                    {showService && <div style={rowStyleMerged}>{row.service}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Services Risk Breakdown Data
 * Matches backend logic from template3/Localhost.js:253-325
 */
export const extractLocalhostServicesRiskBreakdownData = (data) => {
  try {
    // Extract from raw scan data - match backend _Localhost_Services_status logic
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
    
    const rows = [];
    const all = {};

    // Backend: const services = localhost?.['Hardware Informations']?.['Non-Default Services'];
    const services = localhost?.['Hardware Informations']?.['Non-Default Services'];
    
    if (!Array.isArray(services)) {
      return rows;
    }

    // Backend: for (const service of services) { ... all[state] = (all[state] || 0) + 1; }
    for (const service of services) {
      const state = service['State'] || service.State || '';
      if (state) {
        all[state] = (all[state] || 0) + 1;
      }
    }

    // Backend: Object.keys(all).forEach(key => { ... rows.push([key, all[key], severity, explanation]); })
    Object.keys(all).forEach(key => {
      let severity = 'Low';
      let explanation = 'Unknown or inactive state — treated as low risk.';
      
      // Backend: if (key === 'Running') { severity = 'Critical'; explanation = 'Service is currently running. If combined with high privileges or auto-start, it may present an active risk.'; } else if (key === 'Stopped') { severity = 'Moderate'; explanation = 'Service is not running but can be started manually.'; } else if (key === 'Disabled') { severity = 'Low'; explanation = 'Service is disabled and cannot start unless explicitly re-enabled.'; }
      if (key === 'Running') {
        severity = 'Critical';
        explanation = 'Service is currently running. If combined with high privileges or auto-start, it may present an active risk.';
      } else if (key === 'Stopped') {
        severity = 'Moderate';
        explanation = 'Service is not running but can be started manually.';
      } else if (key === 'Disabled') {
        severity = 'Low';
        explanation = 'Service is disabled and cannot start unless explicitly re-enabled.';
      }
      
      // Backend: rows.push([key, all[key], severity, explanation])
      rows.push({
        state: key,
        totalServices: all[key],
        severity: severity,
        explanation: explanation
      });
    });

    return rows;
  } catch (error) {
    console.log('Error extractLocalhostServicesRiskBreakdownData', error);
    return [];
  }
};

/**
 * Localhost - Non-Default Services Risk Breakdown by State Table
 * Displays risk breakdown by service state matching backend template3/Localhost.js:1810-1822
 * Widget auto-sizes to fit all content
 */
/**
 * Localhost - Non-Default Services Risk Breakdown by State Table
 * Displays risk breakdown by service state matching backend template3/Localhost.js:1810-1822
 */
export const LocalhostServicesRiskBreakdownTableWidget = ({ data, config = {}, style = {}, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'State': 'showState', 'Total Services': 'showTotalServices', 'Severity': 'showSeverity', 'Explanation': 'showExplanation' });
  const {
    title = 'Non-Default Services Risk Breakdown by State',
    showState = true,
    showTotalServices = true,
    showSeverity = true,
    showExplanation = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-services-risk-breakdown-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const riskRows = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostServicesRiskBreakdownData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showState, showTotalServices, showSeverity, showExplanation].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1.2fr 1fr 1fr 2.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showState && <div style={headerStyle}>{getDisplayHeader(config, 'State')}</div>}
            {showTotalServices && <div style={headerStyle}>{getDisplayHeader(config, 'Total Services')}</div>}
            {showSeverity && <div style={headerStyle}>{getDisplayHeader(config, 'Severity')}</div>}
            {showExplanation && <div style={headerStyle}>{getDisplayHeader(config, 'Explanation')}</div>}
          </div>
          <div className="open-ports-body">
            {riskRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No non-default services data available.</p>
              </div>
            ) : (
              riskRows.map((row, idx) => {
                const severityColors = {
                  'Critical': theme?.statusColors?.red || '#ef4444',
                  'Moderate': theme?.statusColors?.yellow || '#f59e0b',
                  'Low': theme?.statusColors?.green || '#22c55e'
                };
                const severityColor = severityColors[row.severity] || severityColors.Low;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showState && <div style={rowStyleMerged}>{row.state}</div>}
                    {showTotalServices && <div style={rowStyleMerged}>{row.totalServices}</div>}
                    {showSeverity && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: severityColor, fontWeight: 'bold' }}>
                          {row.severity}
                        </span>
                      </div>
                    )}
                    {showExplanation && <div style={rowStyleMerged}>{row.explanation}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost High-Privilege Services Data
 * Matches backend logic from template2/Localhost.js:1042-1114
 */
export const extractLocalhostHighPrivilegeServicesData = (data) => {
  try {
    // Extract from raw scan data - match backend _Localhost_Services_status logic exactly
    // Backend: const services = localhost?.['Hardware Informations']?.['Non-Default Services']
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
    
    const suspicious = [];
    const ip = localhost?.System?.IP || localhost?.ip || '127.0.0.1';
    const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

    // Backend line 1050: const services = localhost?.['Hardware Informations']?.['Non-Default Services']
    const services = localhost?.['Hardware Informations']?.['Non-Default Services'];
    
    console.log('[LocalhostHighPrivilegeServices] Debug:', {
      hasLocalhost: !!localhost,
      hasHardware: !!localhost?.['Hardware Informations'],
      hardwareKeys: localhost?.['Hardware Informations'] ? Object.keys(localhost['Hardware Informations']) : [],
      servicesCount: Array.isArray(services) ? services.length : 'not array',
      servicesType: typeof services,
      firstService: Array.isArray(services) && services.length > 0 ? services[0] : null,
      firstServiceKeys: Array.isArray(services) && services.length > 0 ? Object.keys(services[0]) : []
    });
    
    // Backend line 1052: if (!Array.isArray(services)) return [rows, suspicious, 1, detail, 0, hasSuspicious];
    if (!Array.isArray(services)) {
      console.log('[LocalhostHighPrivilegeServices] Services is not an array:', services);
      return suspicious;
    }

    // Backend lines 1054-1090: for (const service of services) { ... }
    for (const service of services) {
      // Backend lines 1055-1059: Extract exact field names as backend does
      const account = service['Start Account'];
      const state = service['State'];
      const startMode = service['Start Mode'];
      const DN = service['Display Name'];
      const name = service['Name'] || '';

      console.log('[LocalhostHighPrivilegeServices] Service check:', {
        name,
        account,
        state,
        startMode,
        displayName: DN,
        accountType: typeof account,
        stateType: typeof state,
        startModeType: typeof startMode
      });

      // Backend lines 1066-1079: if (["localSystem", "Administrator"].includes(account)) { if (state === 'Running' && startMode === 'Auto') { ... } }
      if (["localSystem", "Administrator"].includes(account)) {
        if (state === 'Running' && startMode === 'Auto') {
          // Backend lines 1068-1069: risk = 'High'; note = 'Running with auto-start and high privileges';
          const risk = 'High';
          const note = 'Running with auto-start and high privileges';
          
          // Backend lines 1070-1077: suspicious.push([ip, name, DN, 'High privilege service account', risk, note])
          suspicious.push({
            ip: ip,
            hostname: hostname,
            name: name,
            displayName: DN,
            reason: 'High privilege service account',
            risk: risk,
            note: note
          });
          console.log('[LocalhostHighPrivilegeServices] ✓ Added suspicious service:', { name, account, state, startMode });
        } else {
          console.log('[LocalhostHighPrivilegeServices] Service has high privilege account but not Running+Auto:', { 
            name, 
            account, 
            state, 
            startMode,
            stateMatch: state === 'Running',
            startModeMatch: startMode === 'Auto'
          });
        }
      } else {
        console.log('[LocalhostHighPrivilegeServices] Service does not have high privilege account:', { 
          name, 
          account,
          isInList: account ? ["localSystem", "Administrator"].includes(account) : false
        });
      }
    }

    console.log('[LocalhostHighPrivilegeServices] Total suspicious services found:', suspicious.length);
    return suspicious;
  } catch (error) {
    console.log('Error extractLocalhostHighPrivilegeServicesData', error, error.stack);
    return [];
  }
};

/**
 * Localhost - High-Privilege Services Table
 * Displays high-privilege services matching backend template2/Localhost.js:1167-1179
 * Widget auto-sizes to fit all content
 */
/**
 * Localhost - High-Privilege Services Table
 * Displays high-privilege services matching backend template2/Localhost.js:1167-1179
 */
export const LocalhostHighPrivilegeServicesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Name': 'showName', 'Display Name': 'showDisplayName', 'Reason': 'showReason', 'Risk': 'showRisk', 'Note': 'showNote' });
  const {
    title = 'High-Privilege Services',
    showNo = true,
    showIP = true,
    showHostname = true,
    showName = true,
    showDisplayName = true,
    showReason = true,
    showRisk = true,
    showNote = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-high-privilege-services-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allHighPrivilegeData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostHighPrivilegeServicesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayServices = useMemo(() => {
    if (!pagination) return allHighPrivilegeData;
    const slice = getPaginationSlice(pagination);
    return allHighPrivilegeData.slice(slice.startIndex, slice.endIndex);
  }, [allHighPrivilegeData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showName, showDisplayName, showReason, showRisk, showNote].filter(Boolean).length;
  const templateColumns = visibleColumns === 8 
    ? '0.5fr 1fr 1fr 1fr 1.5fr 1.5fr 1fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showReason && <div style={headerStyle}>{getDisplayHeader(config, 'Reason')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
            {showNote && <div style={headerStyle}>{getDisplayHeader(config, 'Note')}</div>}
          </div>
          <div className="open-ports-body">
            {displayServices.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No high-privilege services detected.</p>
              </div>
            ) : (
              displayServices.map((service, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{service.ip || '127.0.0.1'}</div>}
                    {showHostname && <div style={rowStyleMerged}>{service.hostname || 'localhost'}</div>}
                    {showName && <div style={rowStyleMerged}>{service.name || '-'}</div>}
                    {showDisplayName && <div style={rowStyleMerged}>{service.displayName || '-'}</div>}
                    {showReason && <div style={rowStyleMerged}>{service.reason || '-'}</div>}
                    {showRisk && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: service.risk === 'High' ? (theme?.statusColors?.red || '#ef4444') : '#666', fontWeight: 'bold' }}>
                          {service.risk || '-'}
                        </span>
                      </div>
                    )}
                    {showNote && <div style={rowStyleMerged}>{service.note || '-'}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Localhost - Installed Software Table
 * Displays installed software on localhost matching backend template2/Localhost.js:358-380
 */
export const LocalhostInstalledSoftwareTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Device': 'showDevice', 'Name': 'showName', 'Installed Version': 'showVersion', 'Status': 'showStatus' });
  const {
    title = 'Localhost - Installed Software',
    showNo = true,
    showDevice = true,
    showName = true,
    showVersion = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-installed-software-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allSoftwareData = useMemo(() => {
    // Handle wrapped data structure (scanData, data, or direct)
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hardware = localhost['Hardware Informations'] || {};
    const softwares = hardware.installed_software || hardware.softwares || [];

    const extracted = softwares.map((software, index) => {
      const name = software.Name || software.name || '';
      const version = software.Version || software.version || '';

      let status = 'Up to Date';
      if (software.Outdated || version.toLowerCase().includes('old')) {
        status = 'Outdated';
      }

      return {
        no: index + 1,
        device: 'Localhost',
        name,
        installed_version: version,
        status
      };
    });

    return applyTableSort(
      applyTableFilters(extracted, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displaySoftware = useMemo(() => {
    if (!pagination) return allSoftwareData;
    const slice = getPaginationSlice(pagination);
    return allSoftwareData.slice(slice.startIndex, slice.endIndex);
  }, [allSoftwareData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showDevice, showName, showVersion, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1fr 2fr 1.5fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showVersion && <div style={headerStyle}>{getDisplayHeader(config, 'Installed Version')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displaySoftware.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No installed software data available.</p>
              </div>
            ) : (
              displaySoftware.map((software, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDevice && <div style={rowStyleMerged}>{software.device}</div>}
                    {showName && <div style={rowStyleMerged}>{software.name}</div>}
                    {showVersion && <div style={rowStyleMerged}>{software.installed_version}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: software.status === 'Outdated' ? (theme?.statusColors?.yellow || '#fdbc13') : (theme?.statusColors?.green || '#6fca42'), fontWeight: 'bold' }}>
                          {software.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

export const extractLocalhostNonDefaultServicesData = (inputData) => {
  const scanDataSource = inputData?.scanData || inputData?.data || inputData;
  const localhost = scanDataSource?.localhost || {};
  const hardware = localhost['Hardware Informations'] || {};
  const services = hardware.services || hardware['Non-Default Services'] || [];

  const ip = localhost?.System?.IP || localhost?.ip || '127.0.0.1';
  const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

  return services
    .filter(service => service.NonDefault || service.non_default)
    .map((service, index) => ({
      no: index + 1,
      ip: ip,
      hostname: hostname,
      name: service.Name || service.ServiceName || '',
      display_name: service.DisplayName || service.display_name || '',
      reason: service.Reason || service.reason || 'Non-default service',
      state: service.State || service.Status || '',
      start_mode: service.StartMode || service.start_mode || ''
    }));
};

/**
 * Localhost - Non-Default Services Table
 */
/**
 * Localhost - Non-Default Services Table
 */
export const LocalhostNonDefaultServicesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Name': 'showName', 'Display Name': 'showDisplayName', 'Reason': 'showReason', 'State': 'showState', 'Start Mode': 'showStartMode' });
  const {
    title = 'Localhost - Non-Default Services',
    showNo = true,
    showIP = true,
    showHostname = true,
    showName = true,
    showDisplayName = true,
    showReason = true,
    showState = true,
    showStartMode = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-non-default-services-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostNonDefaultServicesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showName, showDisplayName, showReason, showState, showStartMode].filter(Boolean).length;
  const templateColumns = visibleColumns === 8 
    ? '0.5fr 1fr 1fr 1fr 1.5fr 1.5fr 0.8fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showReason && <div style={headerStyle}>{getDisplayHeader(config, 'Reason')}</div>}
            {showState && <div style={headerStyle}>{getDisplayHeader(config, 'State')}</div>}
            {showStartMode && <div style={headerStyle}>{getDisplayHeader(config, 'Start Mode')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No non-default services data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{row.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{row.hostname}</div>}
                    {showName && <div style={rowStyleMerged}>{row.name}</div>}
                    {showDisplayName && <div style={rowStyleMerged}>{row.display_name}</div>}
                    {showReason && <div style={rowStyleMerged}>{row.reason}</div>}
                    {showState && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: row.state === 'Running' ? (theme?.statusColors?.red || '#ef4444') : (theme?.statusColors?.yellow || '#f59e0b'), fontWeight: 'bold' }}>
                          {row.state}
                        </span>
                      </div>
                    )}
                    {showStartMode && <div style={rowStyleMerged}>{row.start_mode}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Localhost - Open Ports Table
 */
export const extractLocalhostOpenPortsData = (inputData) => {
  // Handle wrapped data structure (scanData, data, or direct)
  const scanDataSource = inputData?.scanData || inputData?.data || inputData;
  const localhost = scanDataSource?.localhost || {};
  const hardware = localhost['Hardware Informations'] || {};
  const openPorts = hardware.open_ports || [];

  const ip = localhost?.System?.IP || localhost?.ip || '127.0.0.1';
  const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

  // Sensitive ports list
  const sensitivePorts = {
    21: 'FTP', 22: 'SSH', 23: 'Telnet', 25: 'SMTP',
    53: 'DNS', 80: 'HTTP', 110: 'POP3', 135: 'RPC',
    139: 'NetBIOS', 143: 'IMAP', 443: 'HTTPS', 445: 'SMB',
    1433: 'SQL Server', 1521: 'Oracle', 3306: 'MySQL',
    3389: 'RDP', 5432: 'PostgreSQL', 5900: 'VNC', 8080: 'HTTP-Alt'
  };

  return openPorts.map((port, index) => {
    const portNum = typeof port === 'object' ? port.port : port;
    const isSensitive = sensitivePorts[portNum] ? 'Yes' : 'No';
    const service = sensitivePorts[portNum] || (typeof port === 'object' ? port.service : '');

    return {
      no: index + 1,
      ip: ip,
      hostname: hostname,
      port: portNum,
      is_sensitive: isSensitive,
      service
    };
  });
};

/**
 * Localhost - Open Ports Table
 */
/**
 * Localhost - Open Ports Table
 */
export const LocalhostOpenPortsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Port': 'showPort', 'Is Sensitive': 'showIsSensitive', 'Service': 'showService' });
  const {
    title = 'Localhost - Open Ports',
    showNo = true,
    showIP = true,
    showHostname = true,
    showPort = true,
    showIsSensitive = true,
    showService = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-open-ports-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostOpenPortsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showPort, showIsSensitive, showService].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 1fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showPort && <div style={headerStyle}>{getDisplayHeader(config, 'Port')}</div>}
            {showIsSensitive && <div style={headerStyle}>{getDisplayHeader(config, 'Is Sensitive')}</div>}
            {showService && <div style={headerStyle}>{getDisplayHeader(config, 'Service')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No open ports data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{row.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{row.hostname}</div>}
                    {showPort && <div style={rowStyleMerged}>{row.port}</div>}
                    {showIsSensitive && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: row.is_sensitive === 'Yes' ? (theme?.statusColors?.red || '#ef4444') : '#666', fontWeight: 'bold' }}>
                          {row.is_sensitive}
                        </span>
                      </div>
                    )}
                    {showService && <div style={rowStyleMerged}>{row.service}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Localhost - Browser Passwords Table
 * Shows stored browser credentials
 */
export const extractLocalhostBrowserPasswordsData = (inputData, config) => {
  // Handle wrapped data structure (scanData, data, or direct)
  const scanDataSource = inputData?.scanData || inputData?.data || inputData;
  const localhost = scanDataSource?.localhost || {};
  const browserPasswords = localhost['Browser_Password_analysis'] || {};
  const credentials = browserPasswords.credentials || browserPasswords.passwords || [];

  const ip = localhost?.System?.IP || localhost?.ip || '127.0.0.1';
  const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

  return credentials.map((cred, index) => {
    // Assess password strength
    const password = cred.Password || cred.password || '';
    let strength = 'Unknown';

    if (password) {
      if (password.length < 8) strength = 'Weak';
      else if (password.length < 12) strength = 'Medium';
      else strength = 'Strong';

      // Check for common patterns
      if (/^[a-z]+$/.test(password) || /^[0-9]+$/.test(password)) {
        strength = 'Weak';
      }
    }

    return {
      no: index + 1,
      ip: ip,
      hostname: hostname,
      username: cred.Username || cred.username || '',
      url: cred.URL || cred.url || '',
      strength,
      reuse_count: cred.ReuseCount || cred.reuse_count || 0,
      password: config?.showPasswords ? password : '********'
    };
  });
};

/**
 * Localhost - Browser Passwords Table
 * Shows stored browser credentials
 */
/**
 * Localhost - Browser Passwords Table
 * Shows stored browser credentials
 */
export const LocalhostBrowserPasswordsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Username': 'showUsername', 'URL': 'showURL', 'Strength': 'showStrength', 'Reuse Count': 'showReuseCount', 'Password': 'showPassword' });
  const {
    title = 'Localhost - Stored Browser Credentials',
    showNo = true,
    showIP = true,
    showHostname = true,
    showUsername = true,
    showURL = true,
    showStrength = true,
    showReuseCount = true,
    showPassword = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-browser-passwords-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostBrowserPasswordsData(data, config), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort, config.showPasswords]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showUsername, showURL, showStrength, showReuseCount, showPassword].filter(Boolean).length;
  const templateColumns = visibleColumns === 8 
    ? '0.5fr 1fr 1fr 1.5fr 2fr 1fr 1fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showURL && <div style={headerStyle}>{getDisplayHeader(config, 'URL')}</div>}
            {showStrength && <div style={headerStyle}>{getDisplayHeader(config, 'Strength')}</div>}
            {showReuseCount && <div style={headerStyle}>{getDisplayHeader(config, 'Reuse Count')}</div>}
            {showPassword && <div style={headerStyle}>{getDisplayHeader(config, 'Password')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No browser password data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                const strengthColors = {
                  'Weak': theme?.statusColors?.red || '#d71920',
                  'Medium': theme?.statusColors?.yellow || '#fdbc13',
                  'Strong': theme?.statusColors?.green || '#6fca42'
                };

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{row.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{row.hostname}</div>}
                    {showUsername && <div style={rowStyleMerged}>{row.username}</div>}
                    {showURL && <div style={rowStyleMerged}>{row.url}</div>}
                    {showStrength && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: strengthColors[row.strength] || '#666', fontWeight: 'bold' }}>
                          {row.strength}
                        </span>
                      </div>
                    )}
                    {showReuseCount && <div style={rowStyleMerged}>{row.reuse_count}</div>}
                    {showPassword && <div style={rowStyleMerged}>{row.password}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

// =============================================================================
// MICROSOFT 365 PROOF TABLES (6 widgets)
// =============================================================================

/**
 * Extract inactive MS365 users data from scan results
 * Matches getActiveAndInactiveUserCounts from template2/MS.js:7-18
 */
export function extractInactiveMS365UsersData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  // Backend: const users = data.users || []; const inactive = data.inactive_accounts || [];
  const scanDataSource = data.scanData || data.data || data;
  const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};
  
  // Backend: const inactive = data.inactive_accounts || [];
  const inactive = ms365.inactive_accounts || [];

  if (!Array.isArray(inactive)) return usersData;

  // Backend: for (const user of inactive) { rows.push([user['display_name'], user['user_id']]) }
  // Use exact backend field names first (bracket notation)
  let count = 1;
  for (const user of inactive) {
    usersData.push({
      no: count,
      user_name: user['display_name'] || user.display_name || user.DisplayName || '-',
      user_id: user['user_id'] || user.user_id || user.UserId || user.userId || '-'
    });
    count++;
  }

  return usersData;
}

/**
 * MS365 - Inactive Users Table
 * Shows inactive MS365 users in a table format matching backend
 * Matches Ms_users_Active_status Inactive MS 365 Users table from template2/MS.js:141-153
 * Widget auto-sizes to fit all content
 */
/**
 * MS365 - Inactive Users Table
 * Shows inactive MS365 users in a table format matching backend
 * Matches Ms_users_Active_status Inactive MS 365 Users table from template2/MS.js:141-153
 */
export const MS365InactiveUsersTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'User Name': 'showUserName', 'User ID': 'showUserID' });
  const {
    title = 'Inactive MS 365 Users',
    showUserName = true,
    showUserID = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ms365-inactive-users-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractInactiveMS365UsersData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showUserName, showUserID].filter(Boolean).length;
  const templateColumns = visibleColumns === 2 ? '2fr 3fr' : '1fr';

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showUserName && <div style={headerStyle}>{getDisplayHeader(config, 'User Name')}</div>}
            {showUserID && <div style={headerStyle}>{getDisplayHeader(config, 'User ID')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No inactive users found.</p>
              </div>
            ) : (
              displayData.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showUserName && <div style={rowStyleMerged}>{user.user_name}</div>}
                  {showUserID && <div style={rowStyleMerged}>{user.user_id}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * MS365 - All Users MFA Status Table
 */
/**
 * MS365 - All Users MFA Status Table
 */
/**
 * Extract MS365 All Users MFA data from scan results
 */
export function extractMS365AllUsersMFAData(data) {
  // Handle wrapped data structure (scanData, data, or direct)
  const scanDataSource = data?.scanData || data?.data || data;
  const ms365 = scanDataSource?.MS365 || {};
  const users = ms365.users || [];

  return users.map((user, index) => {
    const mfaEnabled = user.MFAStatus === 'Enabled' || user.StrongAuthenticationMethods?.length > 0 || user.mfa_enabled === true;
    const mfaMethods = user.StrongAuthenticationMethods || [];
    const authMethods = mfaMethods.map(m => m.MethodType || m.Type).join(', ') || 'None';

    return {
      no: index + 1,
      display_name: user.DisplayName || user.display_name || user.username || '',
      mfa_status: mfaEnabled ? 'Enabled' : 'Disabled',
      number_of_mfa_methods: mfaMethods.length,
      authentication: authMethods
    };
  });
}

/**
 * MS365 - All Users MFA Status Table
 */
export const MS365AllUsersMFATableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Display Name': 'showDisplayName', 'MFA Status': 'showMFAStatus', 'Number of MFA Methods': 'showNumMethods', 'Authentication': 'showAuthMethods' });
  const {
    title = 'MS365 All Users MFA Status',
    showNo = true,
    showDisplayName = true,
    showMFAStatus = true,
    showNumMethods = true,
    showAuthMethods = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ms365-all-users-mfa-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractMS365AllUsersMFAData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showDisplayName, showMFAStatus, showNumMethods, showAuthMethods].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 2fr 1fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showMFAStatus && <div style={headerStyle}>{getDisplayHeader(config, 'MFA Status')}</div>}
            {showNumMethods && <div style={headerStyle}>{getDisplayHeader(config, 'Number of MFA Methods')}</div>}
            {showAuthMethods && <div style={headerStyle}>{getDisplayHeader(config, 'Authentication')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No MS365 users found.</p>
              </div>
            ) : (
              displayData.map((user, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                const statusColor = user.mfa_status === 'Enabled' 
                  ? (theme?.statusColors?.green || '#6fca42') 
                  : (theme?.statusColors?.red || '#d71920');

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDisplayName && <div style={rowStyleMerged}>{user.display_name}</div>}
                    {showMFAStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {user.mfa_status}
                        </span>
                      </div>
                    )}
                    {showNumMethods && <div style={rowStyleMerged}>{user.number_of_mfa_methods}</div>}
                    {showAuthMethods && <div style={rowStyleMerged}>{user.authentication}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract users without MFA data
 * Matches getUsersWithoutMFA from template2/MS.js:44-86
 */
export function extractUsersWithoutMFAData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  // Backend: const users = data.MFA || [];
  const scanDataSource = data.scanData || data.data || data;
  const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};
  
  // Backend: const users = data.MFA || [];
  const mfaUsers = ms365.MFA || [];

  if (!Array.isArray(mfaUsers)) return usersData;

  // Backend methodMap for authentication methods
  const methodMap = {
    "#microsoft.graph.passwordAuthenticationMethod": "Password",
    "#microsoft.graph.fido2AuthenticationMethod": "FIDO2 Security Key",
    "#microsoft.graph.microsoftAuthenticatorAuthenticationMethod": "Microsoft Authenticator App",
    "#microsoft.graph.windowsHelloForBusinessAuthenticationMethod": "Windows Hello",
    "#microsoft.graph.phoneAuthenticationMethod": "Phone (SMS or Call)",
    "#microsoft.graph.emailAuthenticationMethod": "Email"
  };

  // Backend: const Disabled = users.filter(user => user["MFA Enabled"] === false)
  // .map(user => ([user["Display Name"], "No MFA", (user["Authentication Methods"] || []).map(method => methodMap[method] || method)])
  const disabledUsers = mfaUsers.filter(user => {
    const mfaStatus = user["MFA Enabled"] !== undefined 
      ? user["MFA Enabled"] 
      : user.mfaEnabled !== undefined 
        ? user.mfaEnabled 
        : user.MFAEnabled !== undefined 
          ? user.MFAEnabled 
          : false;
    return mfaStatus === false;
  });

  for (const user of disabledUsers) {
    const displayName = user["Display Name"] || user.displayName || user.DisplayName || '-';
    const authMethods = user["Authentication Methods"] || user.authenticationMethods || user.AuthenticationMethods || [];
    const formattedMethods = Array.isArray(authMethods)
      ? authMethods.map(method => methodMap[method] || method).join(', ')
      : '-';

    usersData.push({
      display_name: displayName,
      mfa_status: 'No MFA',
      authentication: formattedMethods || '-'
    });
  }

  return usersData;
}

/**
 * MS365 - Users Without MFA Table
 * Shows users without MFA enabled in a table format matching backend
 * Matches Ms_users_MFA_status Users Without MFA table from template2/MS.js:270-282
 * Widget auto-sizes to fit all content
 */
/**
 * MS365 - Users Without MFA Table
 * Shows users without MFA enabled in a table format matching backend
 * Matches Ms_users_MFA_status Users Without MFA table from template2/MS.js:270-282
 */
export const MS365UsersWithoutMFATableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Display Name': 'showDisplayName', 'MFA Status': 'showMFAStatus', 'Authentication': 'showAuthMethods' });
  const {
    title = 'Users Without MFA',
    showNo = true,
    showDisplayName = true,
    showMFAStatus = true,
    showAuthMethods = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ms365-users-without-mfa-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    const extracted = extractUsersWithoutMFAData(data);
    const withNo = extracted.map((u, i) => ({ ...u, no: i + 1 }));
    return applyTableSort(
      applyTableFilters(withNo, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showDisplayName, showMFAStatus, showAuthMethods].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '0.5fr 2fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showMFAStatus && <div style={headerStyle}>{getDisplayHeader(config, 'MFA Status')}</div>}
            {showAuthMethods && <div style={headerStyle}>{getDisplayHeader(config, 'Authentication')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users without MFA found.</p>
              </div>
            ) : (
              displayData.map((user, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDisplayName && <div style={rowStyleMerged}>{user.display_name}</div>}
                    {showMFAStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: theme?.statusColors?.red || '#d71920', fontWeight: 'bold' }}>
                          {user.mfa_status || 'No MFA'}
                        </span>
                      </div>
                    )}
                    {showAuthMethods && <div style={rowStyleMerged}>{user.authentication}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Helper to determine if MS365 data is completely missing or empty
 * Prevents defaulting to failure states when no scan was run.
 */
export const isMS365DataEmpty = (data) => {
  try {
    const scanDataSource = data?.scanData || data?.data || data;
    if (!scanDataSource) return true;

    // Could be named "Microsoft 365 Scan", "MS365", etc.
    const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};
    
    // If it's literally empty, missing, or lacks the policy configuration keys
    const policyKeys = [
      "password_policies",
      "legacy_auth_enabled",
      "conditional_access_policies",
      "lack_of_dlp_policies",
      "misconfigured_exchange_forwarding"
    ];

    // If NONE of the required keys are present at all in the object, assume no scan was run
    const hasAnyPolicyKey = policyKeys.some(key => ms365.hasOwnProperty(key));
    if (!hasAnyPolicyKey) return true;

    return false;
  } catch (error) {
    console.error('Error checking MS365 data emptiness:', error);
    return true; // Default to empty if traversal fails to avoid false negatives
  }
};

/**
 * Extract Policy Configuration Data
 * Matches backend logic from template2/MS.js:321-370
 */
export const extractPolicyConfigurationData = (data) => {
  if (isMS365DataEmpty(data)) return []; 

  try {
    // Extract from raw scan data - match backend PolicyConfigurationStatus logic
    const scanDataSource = data?.scanData || data?.data || data;
    const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};
    
    // Backend: const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"]
    const policyKeys = [
      "password_policies",
      "legacy_auth_enabled",
      "conditional_access_policies",
      "lack_of_dlp_policies",
      "misconfigured_exchange_forwarding"
    ];

    const rows = [];

    // Backend: for (const key of policyKeys) { ... }
    for (const key of policyKeys) {
      let name = "";
      let description = "";
      let status;

      // Backend: if (key === "conditional_access_policies") { status = data[key] === 1 ? "Properly Configured" : "MisConfigured/Disabled"; } else { status = data[key] === 0 ? "Properly Configured" : "MisConfigured/Disabled"; }
      if (key === "conditional_access_policies") {
        status = ms365[key] === 1 ? "Properly Configured" : "No Data/Not Scanned";
      } else {
        status = ms365[key] === 0 ? "Properly Configured" : "No Data/Not Scanned";
      }

      // Backend: switch (key) { ... }
      switch (key) {
        case "password_policies":
          name = "Password Policies";
          description = "Weak password policies are enabled or enforced incorrectly.";
          break;
        case "legacy_auth_enabled":
          name = "Legacy Authentication";
          description = "Legacy authentication protocols are enabled.";
          break;
        case "conditional_access_policies":
          name = "Conditional Access Policies";
          description = "Conditional access policies are configured.";
          break;
        case "lack_of_dlp_policies":
          name = "DLP Policies";
          description = "Data Loss Prevention policies are missing.";
          break;
        case "misconfigured_exchange_forwarding":
          name = "Exchange Forwarding";
          description = "Exchange mailbox forwarding rules are misconfigured.";
          break;
        default:
          // Unknown policy key, use the key as name
          name = key;
          description = "Unknown policy configuration.";
          break;
      }

      // Backend: rows.push([name, description, status])
      rows.push({
        policy_name: name,
        description: description,
        status: status
      });
    }

    return rows;
  } catch (error) {
    console.log('Error extractPolicyConfigurationData', error);
    return [];
  }
};

/**
 * MS365 - Policy Configuration Summary Table
 * Displays policy configuration status matching backend template2/MS.js:416-429
 * Widget auto-sizes to fit all content
 */
/**
 * MS365 - Policy Configuration Summary Table
 * Displays policy configuration status matching backend template2/MS.js:416-429
 */
export const MS365PolicyConfigTableWidget = ({ data, config = {}, style = {}, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Policy Name': 'showPolicyName', 'Description': 'showDescription', 'Status': 'showStatus' });
  const {
    title = 'Policy Configuration Summary',
    showPolicyName = true,
    showDescription = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ms365-policy-config-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const policyRows = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractPolicyConfigurationData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showPolicyName, showDescription, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 3 ? '1.5fr 2fr 1.5fr' : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showPolicyName && <div style={headerStyle}>{getDisplayHeader(config, 'Policy Name')}</div>}
            {showDescription && <div style={headerStyle}>{getDisplayHeader(config, 'Description')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {isMS365DataEmpty(data) ? (
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e2e8f0', color: '#6c757d', minHeight: '150px' }}>
                <span style={{ fontSize: '24px', marginBottom: '8px' }}>⚪</span>
                <p style={{ margin: 0, fontWeight: '500' }}>Microsoft 365 scan not run. No data available.</p>
              </div>
            ) : policyRows.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', color: '#666' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No policy configuration data available.</p>
              </div>
            ) : (
              policyRows.map((row, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showPolicyName && <div style={rowStyleMerged}>{row.policy_name}</div>}
                  {showDescription && <div style={rowStyleMerged}>{row.description}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ 
                        color: row.status === "Properly Configured" 
                          ? (theme?.statusColors?.green || '#22c55e') 
                          : row.status === "No Data/Not Scanned"
                            ? (theme?.statusColors?.gray || '#6b7280')
                            : (theme?.statusColors?.red || '#ef4444'), 
                        fontWeight: 'bold' 
                      }}>
                        {row.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

// =============================================================================
// HIBP/DARK WEB PROOF TABLES (4 widgets)
// =============================================================================

/**
 * HIBP - Breached Credentials Table
 */
/**
 * HIBP - Breached Credentials Table
 */
export const HIBPBreachedCredentialsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'User Email': 'showUserEmail', 'Breach Source': 'showBreachSource', 'Password': 'showPassword', 'Exposure Date': 'showExposureDate', 'Reuse': 'showReuse' });
  const {
    title = 'Dark Web - Breached Credentials',
    showNo = true,
    showUserEmail = true,
    showBreachSource = true,
    showPassword = true,
    showExposureDate = true,
    showReuse = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'hibp-breached-credentials-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    // Handle wrapped data structure (scanData, data, or direct)
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hibpScan = localhost.HIBP_Scan || {};
    const breaches = hibpScan.breaches || hibpScan.credentials || [];

    const extracted = breaches.map((breach, index) => ({
      no: index + 1,
      user_email: breach.Email || breach.email || '',
      breach_source: breach.Source || breach.BreachName || '',
      password: config.showPasswords ? (breach.Password || breach.password || '') : '********',
      exposure_date: breach.BreachDate || breach.Date || '',
      reuse: breach.ReuseCount || breach.reuse || 0
    }));

    return applyTableSort(
      applyTableFilters(extracted, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort, config.showPasswords]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showUserEmail ? '2fr' : '',
    showBreachSource ? '1.5fr' : '',
    showPassword ? '1.5fr' : '',
    showExposureDate ? '1fr' : '',
    showReuse ? '0.5fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showUserEmail && <div style={headerStyle}>{getDisplayHeader(config, 'User Email')}</div>}
            {showBreachSource && <div style={headerStyle}>{getDisplayHeader(config, 'Breach Source')}</div>}
            {showPassword && <div style={headerStyle}>{getDisplayHeader(config, 'Password')}</div>}
            {showExposureDate && <div style={headerStyle}>{getDisplayHeader(config, 'Exposure Date')}</div>}
            {showReuse && <div style={headerStyle}>{getDisplayHeader(config, 'Reuse')}</div>}
          </div>

          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No breached credentials found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showUserEmail && <div style={rowStyleMerged}>{row.user_email}</div>}
                    {showBreachSource && <div style={rowStyleMerged}>{row.breach_source}</div>}
                    {showPassword && <div style={rowStyleMerged}>{row.password}</div>}
                    {showExposureDate && <div style={rowStyleMerged}>{row.exposure_date}</div>}
                    {showReuse && <div style={rowStyleMerged}>{row.reuse}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * HIBP - Credential Exposure by Platform Table
 */
/**
 * HIBP - Credential Exposure by Platform Table
 */
export const HIBPExposureByPlatformTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Platform': 'showPlatform', 'Credentials Found': 'showCredentialsFound' });
  const {
    title = 'Dark Web - Credential Exposure by Platform',
    showPlatform = true,
    showCredentialsFound = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'hibp-exposure-by-platform-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    // Handle wrapped data structure (scanData, data, or direct)
    const scanDataSource = data?.scanData || data?.data || data;
    const localhost = scanDataSource?.localhost || {};
    const hibpScan = localhost.HIBP_Scan || {};
    const breaches = hibpScan.breaches || hibpScan.credentials || [];

    // Aggregate by platform
    const platformMap = {};
    breaches.forEach(breach => {
      const platform = breach.Source || breach.BreachName || 'Unknown';
      if (!platformMap[platform]) {
        platformMap[platform] = 0;
      }
      platformMap[platform]++;
    });

    const extracted = Object.entries(platformMap).map(([platform, count]) => ({
      platform,
      credentials_found: count
    }));

    return applyTableSort(
      applyTableFilters(extracted, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const templateColumns = [
    showPlatform ? '2fr' : '',
    showCredentialsFound ? '1fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showPlatform && <div style={headerStyle}>{getDisplayHeader(config, 'Platform')}</div>}
            {showCredentialsFound && <div style={headerStyle}>{getDisplayHeader(config, 'Credentials Found')}</div>}
          </div>

          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No exposure data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showPlatform && <div style={rowStyleMerged}>{row.platform}</div>}
                  {showCredentialsFound && <div style={rowStyleMerged}>{row.credentials_found}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

// =============================================================================
// PII PROOF TABLES (3 widgets)
// =============================================================================

/**
 * Extract PII Detected Data
 * Matches backend logic from template2/PII.js:9-20
 */
export const extractPIIDetectedData = (data) => {
  try {
    // Extract from raw scan data - match backend getPiiTableData logic
    const scanDataSource = data?.scanData || data?.data || data;
    
    // Backend: function getPiiTableData(combinedPii = {})
    // The PII data is typically in a combinedPii object
    // Try multiple possible paths for PII data
    const combinedPii = scanDataSource?.pii || 
                        scanDataSource?.PII || 
                        scanDataSource?.combinedPii ||
                        scanDataSource?.['PII Detection'] ||
                        scanDataSource?.['PII'] ||
                        {};

    if (!combinedPii || typeof combinedPii !== 'object' || Object.keys(combinedPii).length === 0) {
      return [];
    }

    const rows = [];

    // Backend lines 13-17: Object.entries(combinedPii).forEach(([type, entries]) => { entries.forEach(({ value, source }) => { rows.push([type, value, source]); }); });
    Object.entries(combinedPii).forEach(([type, entries]) => {
      if (!Array.isArray(entries)) return;
      
      entries.forEach((entry) => {
        // Handle both object format { value, source } and array format [value, source]
        const value = entry?.value || entry?.Value || entry?.[0] || entry || '';
        const source = entry?.source || entry?.Source || entry?.Location || entry?.[1] || '';
        
        rows.push({
          type: type,
          value: value,
          source: source
        });
      });
    });

    return rows;
  } catch (error) {
    console.log('Error extractPIIDetectedData', error);
    return [];
  }
};

/**
 * PII Detected Table Widget
 * Displays PII detection data matching backend template2/PII.js:75-88
 * Widget auto-sizes to fit all content
 */
/**
 * PII Detected Table Widget
 * Displays PII detection data matching backend template2/PII.js:75-88
 */
export const PIIDetectedTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Type': 'showType', 'Value': 'showValue', 'Source': 'showSource' });
  const {
    title = 'PII Detected Information',
    showType = true,
    showValue = true,
    showSource = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'pii-detected-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPiiRows = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractPIIDetectedData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const piiRows = useMemo(() => {
    if (!pagination) return allPiiRows;
    const slice = getPaginationSlice(pagination);
    return allPiiRows.slice(slice.startIndex, slice.endIndex);
  }, [allPiiRows, pagination]);

  const visibleColumns = [showType, showValue, showSource].filter(Boolean).length;
  const templateColumns = visibleColumns === 3 ? '1fr 1fr 2.5fr' : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showType && <div style={headerStyle}>{getDisplayHeader(config, 'Type')}</div>}
            {showValue && <div style={headerStyle}>{getDisplayHeader(config, 'Value')}</div>}
            {showSource && <div style={headerStyle}>{getDisplayHeader(config, 'Source')}</div>}
          </div>

          <div className="open-ports-body">
            {piiRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No PII detection.</p>
              </div>
            ) : (
              piiRows.map((row, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showType && <div style={rowStyleMerged}>{row.type}</div>}
                  {showValue && <div style={rowStyleMerged}>{row.value}</div>}
                  {showSource && <div style={rowStyleMerged}>{row.source}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * PII - PII Type Summary Table
 */
export const PIITypeSummaryTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Type': 'showType', 'Total Detected': 'showTotalDetected' });
  const {
    title = 'PII - Finding Summary',
    showType = true,
    showTotalDetected = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'pii-type-summary-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    // Handle wrapped data structure (scanData, data, or direct)
    const scanDataSource = data?.scanData || data?.data || data;
    const piiData = scanDataSource?.pii || scanDataSource?.PII || [];

    // Aggregate by type
    const typeMap = {};
    if (Array.isArray(piiData)) {
      piiData.forEach(pii => {
        const type = pii.Type || pii.type || 'Unknown';
        if (!typeMap[type]) {
          typeMap[type] = 0;
        }
        typeMap[type]++;
      });
    } else if (typeof piiData === 'object' && piiData !== null) {
      Object.entries(piiData).forEach(([type, entries]) => {
        if (Array.isArray(entries)) {
          typeMap[type] = entries.length;
        }
      });
    }

    const extracted = Object.entries(typeMap).map(([type, count]) => ({
      type,
      total_detected: count
    }));

    return applyTableSort(
      applyTableFilters(extracted, config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showType, showTotalDetected].filter(Boolean).length;
  const templateColumns = visibleColumns === 2 ? '2fr 1fr' : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showType && <div style={headerStyle}>{getDisplayHeader(config, 'Type')}</div>}
            {showTotalDetected && <div style={headerStyle}>{getDisplayHeader(config, 'Total Detected')}</div>}
          </div>

          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No PII summary data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showType && <div style={rowStyleMerged}>{row.type}</div>}
                  {showTotalDetected && <div style={rowStyleMerged}>{row.total_detected}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

// =============================================================================
// NEW PROOF TABLES - TEMPLATE1.JS FORMAT
// =============================================================================

/**
 * Extract MS365 Scan Proof data from scan results
 * Based on MS_table_format from template1.js:2811-2842
 */
export function extractMS365ScanProofData(data) {
  const mfaData = [];

  if (!data) return mfaData;

  const scanDataSource = data.scanData || data.data || data;
  const mfaRecords = scanDataSource?.MS365?.MFA || [];

  if (!Array.isArray(mfaRecords) || mfaRecords.length === 0) {
    return mfaData;
  }

  let count = 1;
  for (const record of mfaRecords) {
    const displayName = record["Display Name"] || "-";
    const mfaEnabled = record["MFA Enabled"] === 1 ? "Yes" : "No";
    const numMfaMethods = record["Number of MFA Methods"] ?? "-";
    const authMethodsArray = record["Authentication Methods"];
    const authMethods = Array.isArray(authMethodsArray)
      ? authMethodsArray
          .map(method =>
            method === "#microsoft.graph.passwordAuthenticationMethod"
              ? "passwordAuthentication"
              : method
          )
          .join(", ")
      : "-";

    mfaData.push({
      no: count++,
      display_name: displayName,
      mfa_enabled: mfaEnabled,
      number_of_mfa_methods: numMfaMethods,
      auth_methods: authMethods
    });
  }

  return mfaData;
}

/**
 * MS365 Scan Proof Table Widget
 * Based on MS_table_format from template1.js:2811-2842
 * Uses jsondata?.MS365?.MFA array
 * Widget auto-sizes to fit all content
 */
/**
 * MS365 Scan Proof Table Widget
 * Based on MS_table_format from template1.js:2811-2842
 * Uses jsondata?.MS365?.MFA array
 */
export const MS365ScanProofTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Display Name': 'showDisplayName', 'MFA Enabled': 'showMFAEnabled', 'Num MFA Methods': 'showNumMFAMethods', 'Authentication': 'showAuthMethods' });
  const {
    title = 'Microsoft 365 Scan Proof',
    showNo = true,
    showDisplayName = true,
    showMFAEnabled = true,
    showNumMFAMethods = true,
    showAuthMethods = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ms365-scan-proof-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allMFA = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractMS365ScanProofData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayMFA = useMemo(() => {
    if (!pagination) return allMFA;
    const slice = getPaginationSlice(pagination);
    return allMFA.slice(slice.startIndex, slice.endIndex);
  }, [allMFA, pagination]);

  const visibleColumns = [showNo, showDisplayName, showMFAEnabled, showNumMFAMethods, showAuthMethods].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 2fr 1fr 1.5fr 2fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showMFAEnabled && <div style={headerStyle}>{getDisplayHeader(config, 'MFA Enabled')}</div>}
            {showNumMFAMethods && <div style={headerStyle}>{getDisplayHeader(config, 'Number of MFA Methods')}</div>}
            {showAuthMethods && <div style={headerStyle}>{getDisplayHeader(config, 'Auth Methods')}</div>}
          </div>
          <div className="open-ports-body">
            {displayMFA.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No MFA records found.</p>
              </div>
            ) : (
              displayMFA.map((mfa, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDisplayName && <div style={rowStyleMerged}>{mfa.display_name}</div>}
                    {showMFAEnabled && (
                      <div style={rowStyleMerged}>
                        <span style={{ 
                          color: mfa.mfa_enabled === 'Yes' ? (theme?.statusColors?.green || '#6fca42') : (theme?.statusColors?.red || '#d71920'), 
                          fontWeight: 'bold' 
                        }}>
                          {mfa.mfa_enabled}
                        </span>
                      </div>
                    )}
                    {showNumMFAMethods && <div style={rowStyleMerged}>{mfa.number_of_mfa_methods}</div>}
                    {showAuthMethods && <div style={rowStyleMerged}>{mfa.auth_methods}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Microsoft Policies data from scan results
 * Based on MS_table_format_policies from template1.js:2843-2870
 */
export function extractMicrosoftPoliciesData(data) {
  const policiesData = [];

  if (!data) return policiesData;

  const scanDataSource = data.scanData || data.data || data;
  const ms = scanDataSource?.MS365 || scanDataSource?.["Microsoft 365 Scan"] || {};
  
  const policy = {
    "password_policies": ["Weak Password", 1, "Enabled", "Disabled"],
    "legacy_auth_enabled": ["Legacy Auth", 1, "Enabled", "Disabled"],
    "conditional_access_policies": ["Conditional Access Policies", 1, "Configured", "Not Configured"],
    "excessive_sharing_onedrive_sharepoint": ["Excessive Sharing Onedrive Sharepoint", 1, "Enabled", "Disabled"],
    "lack_of_dlp_policies": ["Data Loss Protection Policy", 1, "Lack", "Found"],
    "data_retention_policies_misconfigured": ["Data Retention Policy", 1, "Misconfigured", "Configured"]
  };

  for (const [key, value] of Object.entries(ms)) {
    if (policy.hasOwnProperty(key)) {
      const status = value === policy[key][1] ? policy[key][2] : policy[key][3];
      policiesData.push({
        policy: policy[key][0],
        status: status
      });
    }
  }

  return policiesData;
}

/**
 * Microsoft Policies Table Widget
 * Based on MS_table_format_policies from template1.js:2843-2870
 * Uses jsondata["MS365"] object
 * Widget auto-sizes to fit all content
 */
/**
 * Microsoft Policies Table Widget
 * Based on MS_table_format_policies from template1.js:2843-2870
 * Uses jsondata["MS365"] object
 */
export const MicrosoftPoliciesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Policy Name': 'showPolicy', 'Status': 'showStatus' });
  const {
    title = 'Microsoft Policies',
    showPolicy = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'microsoft-policies-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPolicies = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractMicrosoftPoliciesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayPolicies = useMemo(() => {
    if (!pagination) return allPolicies;
    const slice = getPaginationSlice(pagination);
    return allPolicies.slice(slice.startIndex, slice.endIndex);
  }, [allPolicies, pagination]);

  const visibleColumns = [showPolicy, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 2 ? '2fr 1.5fr' : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showPolicy && <div style={headerStyle}>{getDisplayHeader(config, 'Policy')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayPolicies.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No policies found.</p>
              </div>
            ) : (
              displayPolicies.map((policy, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showPolicy && <div style={rowStyleMerged}>{policy.policy}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ 
                        color: (policy.status === "Disabled" || policy.status === "Not Configured" || policy.status === "Found" || policy.status === "Configured") 
                          ? (theme?.statusColors?.green || '#6fca42') 
                          : (theme?.statusColors?.red || '#d71920'), 
                        fontWeight: 'bold' 
                      }}>
                        {policy.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Hardware Information data from scan results
 * Based on Localhost_system_table_format from template1.js:2364-2376
 */
export function extractLocalhostHardwareInformationData(data) {
  const hardwareData = [];

  if (!data) return hardwareData;

  const scanDataSource = data.scanData || data.data || data;
  const localhost_sys = scanDataSource?.localhost?.['Hardware Informations'];
  
  if (!localhost_sys) {
    return hardwareData;
  }

  let count = 1;
  Object.entries(localhost_sys).forEach(([key, value]) => {
    hardwareData.push({
      no: count++,
      system_info: key,
      value: Array.isArray(value) ? value.join(', ') : String(value)
    });
  });

  return hardwareData;
}

/**
 * Localhost Hardware Information Table Widget
 * Based on Localhost_system_table_format from template1.js:2364-2376
 * Uses jsondata['localhost']['Hardware Informations']
 * Widget auto-sizes to fit all content
 */
export const LocalhostHardwareInformationTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'System Info': 'showSystemInfo', 'Value': 'showValue' });
  const {
    title = 'Localhost Hardware Information',
    showNo = true,
    showSystemInfo = true,
    showValue = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-hardware-information-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allHardware = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostHardwareInformationData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayHardware = useMemo(() => {
    if (!pagination) return allHardware;
    const slice = getPaginationSlice(pagination);
    return allHardware.slice(slice.startIndex, slice.endIndex);
  }, [allHardware, pagination]);

  const visibleColumns = [showNo, showSystemInfo, showValue].filter(Boolean).length;
  const templateColumns = visibleColumns === 3
    ? '0.5fr 1.5fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showSystemInfo && <div style={headerStyle}>{getDisplayHeader(config, 'System Info')}</div>}
            {showValue && <div style={headerStyle}>{getDisplayHeader(config, 'Value')}</div>}
          </div>

          <div className="open-ports-body">
            {displayHardware.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No hardware information found.</p>
              </div>
            ) : (
              displayHardware.map((item, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showSystemInfo && <div style={rowStyleMerged}>{item.system_info}</div>}
                    {showValue && <div style={rowStyleMerged}>{item.value}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost System Information data from scan results
 * Based on Localhost_systemInfo_table_format from template1.js:2395-2498
 */
export function extractLocalhostSystemInformationData(data) {
  const systemData = [];

  if (!data) return systemData;

  const scanDataSource = data.scanData || data.data || data;
  const localhost = scanDataSource?.localhost || {};
  const devices = localhost['External vulnerability']?.length > 0 
    ? localhost['External vulnerability'][0] 
    : false;

  if (!devices || !Array.isArray(devices)) {
    return systemData;
  }

  let count = 1;
  devices.forEach((device) => {
    const ip = device.ip || "-";
    const hostname = (device.hostname && device.hostname.trim() !== "") ? device.hostname : "-";
    if (ip === "-" || hostname === "-") {
      return; // skip if network info is missing
    }
    const network = `IP: ${ip} | Hostname: ${hostname}`;

    const system = device.system || {};
    const manufacturer = system.Manufacturer || "-";
    const model = system.Model || "-";
    const domain = system.Domain || "-";
    const biosSerial = system["BIOS Serial"] || "-";
    const age = (system.Age !== undefined && system.Age !== null) ? system.Age : "-";

    let diskInfo = "-";
    if (Array.isArray(system["Disk Drives"]) && system["Disk Drives"].length > 0) {
      diskInfo = system["Disk Drives"].map(disk => {
        const dModel = disk.Model || "-";
        const interfaceType = disk.InterfaceType || "-";
        const serialNumber = disk.SerialNumber || "-";
        const status = disk.Status || "-";
        return `Model: ${dModel} | Interface: ${interfaceType} | Serial: ${serialNumber} | Status: ${status}`;
      }).join(" ; ");
    }
    const hardware = `Manufacturer: ${manufacturer} | Model: ${model} | Domain: ${domain} | BIOS Serial: ${biosSerial} | Age: ${age} | Disk Info: ${diskInfo}`;

    const ram = system.RAM ? `RAM: ${system.RAM}` : "-";
    const cpu = system.CPU ? `CPU: ${system.CPU}` : "-";
    const performance = (ram !== "-" && cpu !== "-") ? [ram, cpu].join(" | ") : "-";

    const os = system.OS ? `OS: ${system.OS}` : "-";
    const osVersion = system["OS Version"] ? `Version: ${system["OS Version"]}` : "-";
    const osBuild = system["OS Build"] ? `Build: ${system["OS Build"]}` : "-";
    const osArch = system["OS Architecture"] ? `Arch: ${system["OS Architecture"]}` : "-";
    const osInfo = (os !== "-" && osVersion !== "-" && osBuild !== "-" && osArch !== "-")
      ? [os, osVersion, osBuild, osArch].join(" | ") : "-";

    const activeUser = system["Active User"] ? `Active User: ${system["Active User"]}` : "-";
    const localUsers = (Array.isArray(system["Local Users"]) && system["Local Users"].length > 0)
      ? `Local Users: ${system["Local Users"].join(", ")}` : "-";
    const administrators = (Array.isArray(system.Administrators) && system.Administrators.length > 0)
      ? `Administrators: ${system.Administrators.join(", ")}` : "-";
    const userInfo = (activeUser !== "-" && localUsers !== "-" && administrators !== "-")
      ? [activeUser, localUsers, administrators].join(" | ") : "-";

    // Only include if all required fields are available
    const isComplete = manufacturer !== "-" &&
      model !== "-" &&
      domain !== "-" &&
      biosSerial !== "-" &&
      age !== "-" &&
      diskInfo !== "-" &&
      ram !== "-" &&
      cpu !== "-" &&
      os !== "-" &&
      osVersion !== "-" &&
      osBuild !== "-" &&
      osArch !== "-" &&
      activeUser !== "-" &&
      localUsers !== "-" &&
      administrators !== "-";

    if (isComplete) {
      systemData.push({
        no: count++,
        network: network,
        hardware: hardware,
        performance: performance,
        os_info: osInfo,
        user_info: userInfo
      });
    }
  });

  return systemData;
}

/**
 * Localhost System Information Table Widget
 * Based on Localhost_systemInfo_table_format from template1.js:2395-2498
 * Uses jsondata.localhost['External vulnerability'][0]
 * Widget auto-sizes to fit all content
 */
export const LocalhostSystemInformationTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Network': 'showNetwork', 'Hardware': 'showHardware', 'Performance': 'showPerformance', 'OS Info': 'showOSInfo', 'User Info': 'showUserInfo' });
  const {
    title = 'Localhost System Information',
    showNo = true,
    showNetwork = true,
    showHardware = true,
    showPerformance = true,
    showOSInfo = true,
    showUserInfo = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-system-information-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allSystemInfo = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostSystemInformationData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displaySystemInfo = useMemo(() => {
    if (!pagination) return allSystemInfo;
    const slice = getPaginationSlice(pagination);
    return allSystemInfo.slice(slice.startIndex, slice.endIndex);
  }, [allSystemInfo, pagination]);

  const visibleColumns = [showNo, showNetwork, showHardware, showPerformance, showOSInfo, showUserInfo].filter(Boolean).length;
  const templateColumns = visibleColumns === 6
    ? '0.5fr 1.5fr 2.5fr 1.5fr 2fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showNetwork && <div style={headerStyle}>{getDisplayHeader(config, 'Network')}</div>}
            {showHardware && <div style={headerStyle}>{getDisplayHeader(config, 'Hardware')}</div>}
            {showPerformance && <div style={headerStyle}>{getDisplayHeader(config, 'Performance')}</div>}
            {showOSInfo && <div style={headerStyle}>{getDisplayHeader(config, 'OS Info')}</div>}
            {showUserInfo && <div style={headerStyle}>{getDisplayHeader(config, 'User Info')}</div>}
          </div>

          <div className="open-ports-body">
            {displaySystemInfo.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No system information found.</p>
              </div>
            ) : (
              displaySystemInfo.map((item, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showNetwork && <div style={rowStyleMerged}>{item.network}</div>}
                    {showHardware && <div style={rowStyleMerged}>{item.hardware}</div>}
                    {showPerformance && <div style={rowStyleMerged}>{item.performance}</div>}
                    {showOSInfo && <div style={rowStyleMerged}>{item.os_info}</div>}
                    {showUserInfo && <div style={rowStyleMerged}>{item.user_info}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Installed Applications data from scan results
 * Based on localhost_installedApps_table_format from template1.js:2322-2340
 */
export function extractLocalhostInstalledApplicationsData(data) {
  const appsData = [];

  if (!data) return appsData;

  const scanDataSource = data.scanData || data.data || data;
  const localhost = scanDataSource?.localhost || {};
  
  let installed = null;
  let ip = "-";
  let hostname = "-";

  // Check if we have External vulnerability structure
  const externalVuln = localhost['External vulnerability'];
  if (externalVuln) {
    if (Array.isArray(externalVuln) && externalVuln.length > 0) {
      const localhostDevice = externalVuln[0][0];
      installed = localhostDevice?.installed_softwares || localhostDevice?.installed_applications;
      ip = localhostDevice?.ip || "-";
      hostname = localhostDevice?.hostname && localhostDevice.hostname.trim() !== "" 
        ? localhostDevice.hostname 
        : "-";
    } else if (typeof externalVuln === 'object') {
      installed = externalVuln.installed_applications || externalVuln.installed_softwares;
    }
  }

  // Fallback to top-level localhost properties if not found
  if (!installed) {
    installed = localhost.installed_softwares || localhost.installed_applications;
  }
  if (ip === "-" || hostname === "-") {
    ip = localhost.IP || localhost.ip || ip;
    hostname = localhost.Hostname || localhost.hostname || hostname;
  }

  if (!installed) {
    return appsData;
  }

  let appsList = [];
  if (Array.isArray(installed)) {
    appsList = installed.map(app => {
      let name = app.Name || app.name || '';
      let version = app.Version || app.version || '';
      return {
        Name: typeof name === 'object' ? (name.Name || name.name || JSON.stringify(name)) : name,
        Version: typeof version === 'object' ? (version.Version || version.version || JSON.stringify(version)) : version
      };
    });
  } else if (typeof installed === 'object') {
    appsList = Object.entries(installed).map(([name, val]) => {
      let version = 'Unknown';
      if (val && typeof val === 'object') {
        version = val.Version || val.version || 'Unknown';
      } else {
        version = val || 'Unknown';
      }
      return {
        Name: name,
        Version: String(version)
      };
    });
  }

  let count = 1;
  appsList.forEach((app) => {
    appsData.push({
      no: count++,
      ip: String(ip),
      hostname: String(hostname),
      software_name: app.Name ? String(app.Name) : '-',
      version: app.Version ? String(app.Version) : '-'
    });
  });

  return appsData;
}

/**
 * Localhost Installed Applications Table Widget
 * Based on localhost_installedApps_table_format from template1.js:2322-2340
 * Uses jsondata.localhost['External vulnerability'][0][0].installed_softwares
 * Widget auto-sizes to fit all content
 */
export const LocalhostInstalledApplicationsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'IP': 'showIP', 'Hostname': 'showHostname', 'Software Name': 'showSoftwareName', 'Version': 'showVersion' });
  const {
    title = 'Localhost Installed Applications',
    showNo = true,
    showIP = true,
    showHostname = true,
    showSoftwareName = true,
    showVersion = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-installed-applications-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allApps = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostInstalledApplicationsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayApps = useMemo(() => {
    if (!pagination) return allApps;
    const slice = getPaginationSlice(pagination);
    return allApps.slice(slice.startIndex, slice.endIndex);
  }, [allApps, pagination]);

  const visibleColumns = [showNo, showIP, showHostname, showSoftwareName, showVersion].filter(Boolean).length;
  const templateColumns = visibleColumns === 5
    ? '0.5fr 1fr 1.5fr 2fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showSoftwareName && <div style={headerStyle}>{getDisplayHeader(config, 'Software Name')}</div>}
            {showVersion && <div style={headerStyle}>{getDisplayHeader(config, 'Version')}</div>}
          </div>

          <div className="open-ports-body">
            {displayApps.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No installed applications found.</p>
              </div>
            ) : (
              displayApps.map((app, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{app.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{app.hostname}</div>}
                    {showSoftwareName && <div style={rowStyleMerged}>{app.software_name}</div>}
                    {showVersion && <div style={rowStyleMerged}>{app.version}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Localhost Event Logs data from scan results
 * Based on localhost_events_table_format from template1.js:2341-2363
 */
export function extractLocalhostEventLogsData(data) {
  const eventLogsData = [];

  if (!data) return eventLogsData;

  const scanDataSource = data.scanData || data.data || data;
  const localhost = scanDataSource?.localhost || {};
  const event_logs = localhost.Analyze_event_logs;
  const event_log = (event_logs || []).length > 0 ? event_logs[0] : false;

  if (!event_log || !Array.isArray(event_log)) {
    return eventLogsData;
  }

  let count = 1;
  event_log.forEach((event) => {
    const eventTime = event.timestamp || event["Time Generated"];
    const timestamp = eventTime ? eventTime.replace('T', ' ') : '-';
    const event_id = event.event_id || event["Event ID"] || '-';
    const event_type = event.event_type || event["Event Type"] || '-';
    const source = event.source || event["Source Name"] || '-';
    const category = event.category || event["Event Category"] || '-';
    const record_number = event.record_number || '-';
    const message = event.message || '-';
    
    // Sanitize text (remove newlines and non-ASCII)
    const sanitizeText = (text) => {
      return String(text)
        .replace(/[\r\n]+/g, ' ')
        .replace(/[^\x20-\x7E]+/g, '')
        .trim();
    };
    
    // Truncate message
    const truncateText = (text, maxLength = 500) => {
      const safeText = String(text || '').replace(/[\r\n]+/g, ' ').replace(/[^\x20-\x7E]+/g, '');
      return safeText.length > maxLength ? safeText.slice(0, maxLength) + '...' : safeText;
    };

    eventLogsData.push({
      no: count++,
      timestamp: timestamp,
      event_id: event_id,
      event_type: event_type,
      source: sanitizeText(source),
      category: sanitizeText(category),
      record_number: record_number,
      message: truncateText(message)
    });
  });

  return eventLogsData;
}

/**
 * Localhost Event Logs Table Widget
 * Based on localhost_events_table_format from template1.js:2341-2363
 * Uses jsondata["localhost"].Analyze_event_logs[0]
 * Widget auto-sizes to fit all content
 */
export const LocalhostEventLogsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Timestamp': 'showTimestamp', 'Event ID': 'showEventID', 'Event Type': 'showEventType', 'Source': 'showSource', 'Category': 'showCategory', 'Record Number': 'showRecordNumber', 'Message': 'showMessage' });
  const {
    title = 'Localhost Event Logs',
    showNo = true,
    showTimestamp = true,
    showEventID = true,
    showEventType = true,
    showSource = true,
    showCategory = true,
    showRecordNumber = true,
    showMessage = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-event-logs-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allEvents = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostEventLogsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayEvents = useMemo(() => {
    if (!pagination) return allEvents;
    const slice = getPaginationSlice(pagination);
    return allEvents.slice(slice.startIndex, slice.endIndex);
  }, [allEvents, pagination]);

  const visibleColumns = [
    showNo, showTimestamp, showEventID, showEventType,
    showSource, showCategory, showRecordNumber, showMessage
  ].filter(Boolean).length;

  const templateColumns = visibleColumns === 8
    ? '0.5fr 1.5fr 1fr 1fr 1.5fr 1.5fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showTimestamp && <div style={headerStyle}>{getDisplayHeader(config, 'Timestamp')}</div>}
            {showEventID && <div style={headerStyle}>{getDisplayHeader(config, 'Event ID')}</div>}
            {showEventType && <div style={headerStyle}>{getDisplayHeader(config, 'Event Type')}</div>}
            {showSource && <div style={headerStyle}>{getDisplayHeader(config, 'Source')}</div>}
            {showCategory && <div style={headerStyle}>{getDisplayHeader(config, 'Category')}</div>}
            {showRecordNumber && <div style={headerStyle}>{getDisplayHeader(config, 'Record Number')}</div>}
            {showMessage && <div style={headerStyle}>{getDisplayHeader(config, 'Message')}</div>}
          </div>

          <div className="open-ports-body">
            {displayEvents.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No event logs found.</p>
              </div>
            ) : (
              displayEvents.map((event, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showTimestamp && <div style={rowStyleMerged}>{event.timestamp}</div>}
                    {showEventID && <div style={rowStyleMerged}>{event.event_id}</div>}
                    {showEventType && <div style={rowStyleMerged}>{event.event_type}</div>}
                    {showSource && <div style={rowStyleMerged}>{event.source}</div>}
                    {showCategory && <div style={rowStyleMerged}>{event.category}</div>}
                    {showRecordNumber && <div style={rowStyleMerged}>{event.record_number}</div>}
                    {showMessage && <div style={rowStyleMerged}>{event.message}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

export function extractLocalhostOpenPortsAuditData(data) {
  const portsData = [];

  if (!data) return portsData;

  const scanDataSource = data.scanData || data.data || data;
  const localhost = scanDataSource?.localhost || {};
  
  if (!localhost || !localhost['External vulnerability'] || Object.keys(localhost['External vulnerability']).length === 0) {
    return portsData;
  }

  // Handle the format where External vulnerability might be an array or object
  let localhostDevice = {};
  if (Array.isArray(localhost['External vulnerability']) && Array.isArray(localhost['External vulnerability'][0])) {
    localhostDevice = localhost['External vulnerability'][0][0];
  } else if (Array.isArray(localhost['External vulnerability'])) {
    localhostDevice = localhost['External vulnerability'][0];
  } else {
    localhostDevice = localhost['External vulnerability'];
  }

  const sensitive_ports = { 
    21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP", 53: "DNS", 
    80: "HTTP", 139: "NetBIOS", 143: "SMB", 3306: "MySQL", 
    3389: "RDP", 8080: "HTTP" 
  };

  const device_type = localhost['Device Type'] || '-';
  const open_ports = localhostDevice?.open_ports || {};

  const ip = localhost?.System?.IP || localhost?.ip || '127.0.0.1';
  const hostname = localhost?.System?.Hostname || localhost?.hostname || 'localhost';

  let count = 1;

  // open_ports format could be { [ip]: { [portNum]: "status" } } or just { [portNum]: "status" }
  // Let's iterate through IPs if it's nested, or just ports directly.
  const isNested = Object.keys(open_ports).length > 0 && typeof Object.values(open_ports)[0] === 'object';
  
  if (isNested) {
    Object.keys(open_ports).forEach((targetIp) => {
      const ports = open_ports[targetIp];
      Object.keys(ports).forEach((port) => {
        portsData.push({
          no: count++,
          ip: targetIp,
          hostname: hostname,
          device_name: device_type,
          open_ports: port,
          sensitive_port: sensitive_ports.hasOwnProperty(parseInt(port)) ? 'Yes' : 'No'
        });
      });
    });
  } else {
    Object.keys(open_ports).forEach((port) => {
      portsData.push({
        no: count++,
        ip: ip,
        hostname: hostname,
        device_name: device_type,
        open_ports: port,
        sensitive_port: sensitive_ports.hasOwnProperty(parseInt(port)) ? 'Yes' : 'No'
      });
    });
  }

  return portsData;
}

/**
 * Localhost Open Ports Audit Table Widget
 * Based on Localhost_port_table_format from template1.js:2377-2394
 * Uses jsondata['localhost']['External vulnerability'][0][0].open_ports
 * Widget auto-sizes to fit all content
 */
export const LocalhostOpenPortsAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Device Name': 'showDeviceName', 'Open Ports': 'showOpenPorts', 'Sensitive Port': 'showSensitivePort' });
  const {
    title = 'Audit Localhost Open Ports',
    showNo = true,
    showIP = true,
    showHostname = true,
    showDeviceName = true,
    showOpenPorts = true,
    showSensitivePort = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'localhost-open-ports-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPorts = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalhostOpenPortsAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayPorts = useMemo(() => {
    if (!pagination) return allPorts;
    const slice = getPaginationSlice(pagination);
    return allPorts.slice(slice.startIndex, slice.endIndex);
  }, [allPorts, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showIP ? '1fr' : '',
    showHostname ? '1fr' : '',
    showDeviceName ? '1.5fr' : '',
    showOpenPorts ? '1.5fr' : '',
    showSensitivePort ? '1fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showDeviceName && <div style={headerStyle}>{getDisplayHeader(config, 'Device Name')}</div>}
            {showOpenPorts && <div style={headerStyle}>{getDisplayHeader(config, 'Open Ports')}</div>}
            {showSensitivePort && <div style={headerStyle}>{getDisplayHeader(config, 'Sensitive Port')}</div>}
          </div>

          <div className="open-ports-body">
            {displayPorts.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No open ports found.</p>
              </div>
            ) : (
              displayPorts.map((port, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div style={rowStyleMerged}>{port.ip}</div>}
                    {showHostname && <div style={rowStyleMerged}>{port.hostname}</div>}
                    {showDeviceName && <div style={rowStyleMerged}>{port.device_name}</div>}
                    {showOpenPorts && <div style={rowStyleMerged}>{port.open_ports}</div>}
                    {showSensitivePort && (
                      <div style={{
                        ...rowStyleMerged,
                        color: port.sensitive_port === 'Yes' ? '#d71920' : '#6fca42',
                        fontWeight: 'bold'
                      }}>
                        {port.sensitive_port}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Users audit data from scan results
 * Based on AD_table_format_users from template1.js:2501-2559
 */
export function extractADUsersAuditData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
  
  // Try both patterns: direct array (as per backend) and nested [0] (as per working widget)
  let users = adData?.["AD Users"] || adData?.['AD Users'];
  
  // If it's an array with first element being an array, use that (matching ADAllUsersTableWidget)
  if (Array.isArray(users) && users.length > 0 && Array.isArray(users[0])) {
    users = users[0];
  }
  
  // Fallback: if users is not an array or empty, try [0] pattern
  if (!Array.isArray(users) || users.length === 0) {
    users = adData?.["AD Users"]?.[0] || adData?.['AD Users']?.[0] || [];
  }

  if (!Array.isArray(users) || users.length === 0) {
    return usersData;
  }

  let count = 1;
  for (const user of users) {
    usersData.push({
      no: count,
      username: user.username || '-',
      display_name: user.display_name || '-',
      user_principal_name: user.user_principal_name || '-',
      password_never_expires: user.password_never_expires === 1,
      password_last_set: user.password_last_set || '-',
      bad_password_count: user.bad_password_count ?? '-',
      bad_password_time: (user.bad_password_time || '').split('T')[0] || '-',
      account_flags: user.account_flags || [],
      last_logon: user.last_logon || '-',
      lockout_time: user.lockout_time || '-',
      account_created: (user.account_created || '').split('T')[0] || '-',
      account_expires: (user.account_expires || '').split('T')[0] || '-',
      is_disabled: user.is_disabled === 1,
      is_admin: user.is_admin === 1
    });
    count++;
  }

  return usersData;
}

/**
 * AD Users Audit Table Widget
 * Based on AD_table_format_users from template1.js:2501-2559
 * Uses jsondata["Ad Scan"]["AD Users"]
 * Widget auto-sizes to fit all content
 */
export const ADUsersAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const displayMode = config.displayMode || 'basic'; // 'basic' or 'advanced'

  const hiddenOverrides = applyHiddenColumns(config, { 
    '#': 'showNo', 
    'User': 'showUser',
    'Status': 'showStatus',
    'Last Logon': 'showLastLogon',
    'Created': 'showCreated',
    'User Info': 'showUserInfo', 
    'Password Info': 'showPasswordInfo',
    'Account Flags': 'showAccountFlags',
    'Logon Info': 'showLogonInfo',
    'Account Lifecycle': 'showAccountLifecycle'
  });

  const {
    title = 'Audit AD Users',
    showNo = true,
    // Basic columns
    showUser = true,
    showStatus = true,
    showLastLogon = true,
    showCreated = true,
    // Advanced columns
    showUserInfo = true,
    showPasswordInfo = true,
    showAccountFlags = true,
    showLogonInfo = true,
    showAccountLifecycle = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-users-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);

  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);

  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allUsers = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADUsersAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayUsers = useMemo(() => {
    if (!pagination) return allUsers;
    const slice = getPaginationSlice(pagination);
    return allUsers.slice(slice.startIndex, slice.endIndex);
  }, [allUsers, pagination]);

  // Define columns based on mode
  const templateColumns = useMemo(() => {
    const cols = [];
    if (showNo) cols.push('50px');

    if (displayMode === 'basic') {
      if (showUser) cols.push('2fr');
      if (showStatus) cols.push('1.5fr');
      if (showLastLogon) cols.push('1.2fr');
      if (showCreated) cols.push('1.2fr');
    } else {
      if (showUserInfo) cols.push('2fr');
      if (showPasswordInfo) cols.push('2fr');
      if (showAccountFlags) cols.push('1.5fr');
      if (showLogonInfo) cols.push('1.5fr');
      if (showAccountLifecycle) cols.push('1.5fr');
    }
    return cols.join(' ') || '1fr';
  }, [displayMode, showNo, showUser, showStatus, showLastLogon, showCreated, showUserInfo, showPasswordInfo, showAccountFlags, showLogonInfo, showAccountLifecycle]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '10px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: '2px'
  };

  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    backgroundColor: '#f8f9fa'
  };

  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const renderStatusBadges = (user) => {
    const badges = [];
    if (user.is_admin) badges.push(<span key="admin" style={{ color: '#d32f2f', fontWeight: 'bold' }}>[ADMIN]</span>);
    if (user.is_disabled) badges.push(<span key="disabled" style={{ color: '#757575', fontStyle: 'italic' }}>[DISABLED]</span>);
    if (user.password_never_expires) badges.push(<span key="never-ext" style={{ color: '#f57c00' }}>[PWD NEVER EXPIRES]</span>);
    
    if (badges.length === 0) return <span>Normal</span>;
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{badges}</div>;
  };

  return (
    <div className="ad-users-audit-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="ad-users-audit-table" style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
          <div className="ad-users-header" style={{ 
            display: 'grid', 
            gridTemplateColumns: templateColumns, 
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            {showNo && <div style={headerStyle}>#</div>}
            
            {displayMode === 'basic' ? (
              <>
                {showUser && <div style={headerStyle}>User</div>}
                {showStatus && <div style={headerStyle}>Status</div>}
                {showLastLogon && <div style={headerStyle}>Last Logon</div>}
                {showCreated && <div style={headerStyle}>Created</div>}
              </>
            ) : (
              <>
                {showUserInfo && <div style={headerStyle}>User Details</div>}
                {showPasswordInfo && <div style={headerStyle}>Password Status</div>}
                {showAccountFlags && <div style={headerStyle}>Account Flags</div>}
                {showLogonInfo && <div style={headerStyle}>Logon Activity</div>}
                {showAccountLifecycle && <div style={headerStyle}>lifecycle</div>}
              </>
            )}
          </div>

          <div className="ad-users-body">
            {displayUsers.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d', backgroundColor: '#fff' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No Active Directory users found in this scan.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="ad-users-row" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: templateColumns,
                    borderBottom: '1px solid #eee',
                    backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                  }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    
                    {displayMode === 'basic' ? (
                      <>
                        {showUser && (
                          <div style={rowStyleMerged}>
                            <div style={{ fontWeight: '600' }}>{user.display_name}</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>{user.username}</div>
                          </div>
                        )}
                        {showStatus && <div style={rowStyleMerged}>{renderStatusBadges(user)}</div>}
                        {showLastLogon && <div style={rowStyleMerged}>{user.last_logon}</div>}
                        {showCreated && <div style={rowStyleMerged}>{user.account_created}</div>}
                      </>
                    ) : (
                      <>
                        {showUserInfo && (
                          <div style={rowStyleMerged}>
                            <div><strong>Name:</strong> {user.display_name}</div>
                            <div><strong>User:</strong> {user.username}</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>{user.user_principal_name}</div>
                          </div>
                        )}
                        {showPasswordInfo && (
                          <div style={rowStyleMerged}>
                            <div><strong>Never Expires:</strong> {user.password_never_expires ? 'Yes' : 'No'}</div>
                            <div><strong>Last Set:</strong> {user.password_last_set}</div>
                            <div style={{ fontSize: '0.85em' }}><strong>Bad Attempts:</strong> {user.bad_password_count}</div>
                          </div>
                        )}
                        {showAccountFlags && (
                          <div style={rowStyleMerged}>
                            {(user.account_flags || []).length > 0 ? (
                              user.account_flags.map((flag, fidx) => <div key={fidx} style={{ fontSize: '0.85em' }}>• {flag}</div>)
                            ) : '-'}
                          </div>
                        )}
                        {showLogonInfo && (
                          <div style={rowStyleMerged}>
                            <div><strong>Last Logon:</strong> {user.last_logon}</div>
                            <div><strong>Lockout:</strong> {user.lockout_time}</div>
                          </div>
                        )}
                        {showAccountLifecycle && (
                          <div style={rowStyleMerged}>
                            <div><strong>Created:</strong> {user.account_created}</div>
                            <div><strong>Expires:</strong> {user.account_expires}</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Computers audit data from scan results
 * Based on AD_table_format_Computers from template1.js:2560-2580
 */
export function extractADComputersAuditData(data) {
  const computersData = [];

  if (!data) return computersData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const computers = adData?.Computers || [];

  if (!Array.isArray(computers) || computers.length === 0) {
    return computersData;
  }

  let count = 1;
  for (const item of computers) {
    computersData.push({
      no: count,
      computer: item.name || '-',
      hostname: item.Host || "-",
      operating_system: item.os || "-",
      os_version: item["os version"] || "-",
      last_login: item.last_logon || '-',
      last_password_set: item.pwd_last_set || '-',
      created: (item.created || '').split('T')[0] || '-',
      flags: item.account_flags || '-',
      disabled: item.is_disabled === 1 ? 'Yes' : 'No'
    });
    count++;
  }

  return computersData;
}

/**
 * AD Computers Audit Table Widget
 * Based on AD_table_format_Computers from template1.js:2560-2580
 * Uses jsondata["Ad Scan"].Computers
 * Widget auto-sizes to fit all content
 */
export const ADComputersAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Computer': 'showComputer', 'Hostname': 'showHostname', 'Operating System': 'showOS', 'OS Version': 'showOSVersion', 'Last Login': 'showLastLogin', 'Last Password Set': 'showLastPasswordSet', 'Created': 'showCreated', 'Flags': 'showFlags', 'Disabled': 'showDisabled' });
  const {
    title = 'Audit AD Computers',
    showNo = true,
    showComputer = true,
    showHostname = true,
    showOS = true,
    showOSVersion = true,
    showLastLogin = true,
    showLastPasswordSet = true,
    showCreated = true,
    showFlags = true,
    showDisabled = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-computers-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allComputers = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADComputersAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayComputers = useMemo(() => {
    if (!pagination) return allComputers;
    const slice = getPaginationSlice(pagination);
    return allComputers.slice(slice.startIndex, slice.endIndex);
  }, [allComputers, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showComputer ? '1.5fr' : '',
    showHostname ? '1.5fr' : '',
    showOS ? '1.5fr' : '',
    showOSVersion ? '1fr' : '',
    showLastLogin ? '1.5fr' : '',
    showLastPasswordSet ? '1.5fr' : '',
    showCreated ? '1fr' : '',
    showFlags ? '1.5fr' : '',
    showDisabled ? '1fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showComputer && <div style={headerStyle}>{getDisplayHeader(config, 'Computer')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showOS && <div style={headerStyle}>{getDisplayHeader(config, 'Operating System')}</div>}
            {showOSVersion && <div style={headerStyle}>{getDisplayHeader(config, 'OS Version')}</div>}
            {showLastLogin && <div style={headerStyle}>{getDisplayHeader(config, 'Last Login')}</div>}
            {showLastPasswordSet && <div style={headerStyle}>{getDisplayHeader(config, 'Last Password Set')}</div>}
            {showCreated && <div style={headerStyle}>{getDisplayHeader(config, 'Created')}</div>}
            {showFlags && <div style={headerStyle}>{getDisplayHeader(config, 'Flags')}</div>}
            {showDisabled && <div style={headerStyle}>{getDisplayHeader(config, 'Disabled')}</div>}
          </div>

          <div className="open-ports-body">
            {displayComputers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No AD computers found.</p>
              </div>
            ) : (
              displayComputers.map((computer, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputer && <div style={rowStyleMerged}>{computer.computer}</div>}
                    {showHostname && <div style={rowStyleMerged}>{computer.hostname}</div>}
                    {showOS && <div style={rowStyleMerged}>{computer.operating_system}</div>}
                    {showOSVersion && <div style={rowStyleMerged}>{computer.os_version}</div>}
                    {showLastLogin && <div style={rowStyleMerged}>{computer.last_login}</div>}
                    {showLastPasswordSet && <div style={rowStyleMerged}>{computer.last_password_set}</div>}
                    {showCreated && <div style={rowStyleMerged}>{computer.created}</div>}
                    {showFlags && <div style={rowStyleMerged}>{computer.flags}</div>}
                    {showDisabled && <div style={rowStyleMerged}>{computer.disabled}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Devices audit data from scan results
 * Based on AD_table_format_devices from template1.js:2603-2687
 */
export function extractADDevicesAuditData(data) {
  const devicesData = [];

  if (!data) return devicesData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const devices = adData["ad_devices"] || {};

  if (!devices || typeof devices !== 'object' || Object.keys(devices).length === 0) {
    return devicesData;
  }

  for (const [deviceName, device] of Object.entries(devices)) {
    // System Info grouping
    let cpuName = "-", cpuCores = "-", totalRAM = "-", diskDrive = "-",
        diskFileSystem = "-", diskTotalSpace = "-", diskFreeSpace = "-", backup = "-";

    if (device.system_result) {
      if (device.system_result.cpu && device.system_result.cpu.length > 0) {
        cpuName = device.system_result.cpu[0].Name || "-";
        cpuCores = device.system_result.cpu[0].Cores || "-";
      }
      if (device.system_result.ram) {
        totalRAM = device.system_result.ram["Total RAM(GB)"] || "-";
      }
      if (device.system_result.disk && device.system_result.disk.length > 0) {
        diskDrive = device.system_result.disk[0].Drive || "-";
        diskFileSystem = device.system_result.disk[0]["File System"] || "-";
        diskTotalSpace = device.system_result.disk[0]["Total Space (GB)"] || "-";
        diskFreeSpace = device.system_result.disk[0]["Free Space"] || "-";
      }
      if (device.system_result.backup) {
        backup = device.system_result.backup;
      }
    }

    // DNS Info grouping
    let dnsARecordStatus = "-", dnsMXRecordStatus = "-", dnsSOARecordStatus = "-",
        dnsReverseDNSStatus = "-", dnsCheckStatus = "-";
    if (device.dns_result && device.dns_result.hostname) {
      dnsARecordStatus = device.dns_result.a_record_status || "-";
      dnsMXRecordStatus = device.dns_result.mx_record_status || "-";
      dnsSOARecordStatus = device.dns_result.soa_record_status || "-";
      dnsReverseDNSStatus = device.dns_result.reverse_dns_status || "-";
      dnsCheckStatus = device.dns_result.dns_check_status || "-";
    }

    // RDP Info grouping
    let rdpStatus = "-", firewallStatus = "-";
    if (device.rdp_result && device.rdp_result.hostname) {
      rdpStatus = device.rdp_result.rdp_status || "-";
      firewallStatus = (device.rdp_result.firewall_status !== null && device.rdp_result.firewall_status !== undefined)
        ? device.rdp_result.firewall_status
        : "-";
    }

    devicesData.push({
      device_name: deviceName,
      dns_hostname: device.dNSHostName || "-",
      device_ip: device.device_ip || "-",
      // System info
      cpu_name: cpuName,
      cpu_cores: cpuCores,
      ram: totalRAM,
      disk_drive: diskDrive,
      disk_fs: diskFileSystem,
      disk_total: diskTotalSpace,
      disk_free: diskFreeSpace,
      backup: backup,
      // DNS info
      dns_a: dnsARecordStatus,
      dns_mx: dnsMXRecordStatus,
      dns_soa: dnsSOARecordStatus,
      dns_reverse: dnsReverseDNSStatus,
      dns_check: dnsCheckStatus,
      // RDP info
      rdp_status: rdpStatus,
      firewall_status: firewallStatus
    });
  }

  return devicesData;
}

/**
 * AD Devices Audit Table Widget
 * Based on AD_table_format_devices from template1.js:2603-2687
 * Uses jsondata['Ad Scan']["ad_devices"]
 * Widget auto-sizes to fit all content
 */
export const ADDevicesAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device Name': 'showDeviceName', 'DNS HostName': 'showDNSHostname', 'Device IP': 'showDeviceIP', 'System Info': 'showSystemInfo', 'Backup': 'showBackup', 'DNS Info': 'showDNSInfo', 'RDP Info': 'showRDPInfo' });
  const {
    title = 'Audit AD Devices',
    showDeviceName = true,
    showDNSHostname = true,
    showDeviceIP = true,
    showSystemInfo = true,
    showBackup = true,
    showDNSInfo = true,
    showRDPInfo = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-devices-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allDevices = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADDevicesAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayDevices = useMemo(() => {
    if (!pagination) return allDevices;
    const slice = getPaginationSlice(pagination);
    return allDevices.slice(slice.startIndex, slice.endIndex);
  }, [allDevices, pagination]);

  const templateColumns = [
    showDeviceName ? '1.5fr' : '',
    showDNSHostname ? '1.5fr' : '',
    showDeviceIP ? '1fr' : '',
    showSystemInfo ? '2.5fr' : '',
    showBackup ? '1fr' : '',
    showDNSInfo ? '2fr' : '',
    showRDPInfo ? '1.5fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '10px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    backgroundColor: '#f8f9fa'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="ad-devices-audit-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="ad-devices-audit-table" style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
          <div className="ad-devices-header" style={{ 
            display: 'grid', 
            gridTemplateColumns: templateColumns,
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            {showDeviceName && <div style={headerStyle}>Device Name</div>}
            {showDNSHostname && <div style={headerStyle}>DNS HostName</div>}
            {showDeviceIP && <div style={headerStyle}>Device IP</div>}
            {showSystemInfo && <div style={headerStyle}>System Info</div>}
            {showBackup && <div style={headerStyle}>Backup</div>}
            {showDNSInfo && <div style={headerStyle}>DNS Status</div>}
            {showRDPInfo && <div style={headerStyle}>RDP Status</div>}
          </div>

          <div className="ad-devices-body">
            {displayDevices.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#fff' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No AD devices found.</p>
              </div>
            ) : (
              displayDevices.map((device, idx) => (
                <div key={idx} className="ad-devices-row" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: templateColumns,
                  borderBottom: '1px solid #eee',
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                }}>
                  {showDeviceName && <div style={rowStyleMerged}>{device.device_name}</div>}
                  {showDNSHostname && <div style={rowStyleMerged}>{device.dns_hostname}</div>}
                  {showDeviceIP && <div style={rowStyleMerged}>{device.device_ip}</div>}
                  {showSystemInfo && (
                    <div style={rowStyleMerged}>
                      <div><strong>CPU:</strong> {device.cpu_name} ({device.cpu_cores} cores)</div>
                      <div><strong>RAM:</strong> {device.ram}GB</div>
                      <div style={{ fontSize: '0.85em', color: '#666' }}>
                        <strong>Disk:</strong> {device.disk_drive} ({device.disk_fs}, {device.disk_total}GB total)
                      </div>
                    </div>
                  )}
                  {showBackup && <div style={rowStyleMerged}>{device.backup}</div>}
                  {showDNSInfo && (
                    <div style={rowStyleMerged}>
                      <div><strong>A:</strong> {device.dns_a}</div>
                      <div><strong>MX:</strong> {device.dns_mx}</div>
                      <div style={{ fontSize: '0.85em' }}><strong>Check:</strong> {device.dns_check}</div>
                    </div>
                  )}
                  {showRDPInfo && (
                    <div style={rowStyleMerged}>
                      <div><strong>RDP:</strong> {device.rdp_status}</div>
                      <div><strong>FW:</strong> {device.firewall_status}</div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Groups audit data from scan results
 * Based on ad_groups_table_format from template1.js:2706-2739
 */
export function extractADGroupsAuditData(data) {
  const groupsData = [];

  if (!data) return groupsData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const groups = adData.groups || [];

  if (!Array.isArray(groups) || groups.length === 0) {
    return groupsData;
  }

  let count = 1;
  for (const group of groups) {
    groupsData.push({
      no: count++,
      group_name: group.name || "-",
      created: group.whenCreated ? group.whenCreated.split('T')[0] : "-",
      distinguished_name: group.distinguishedName || "-",
      security_group: group.isSecurityGroup === 1 ? 'Yes' : 'No',
      managed_by: group.managedBy || "-",
      members: Array.isArray(group.members) ? group.members : (group.members ? [group.members.toString()] : []),
      description: group.description || "-"
    });
  }

  return groupsData;
}

/**
 * AD Groups Audit Table Widget
 * Based on ad_groups_table_format from template1.js:2706-2739
 * Uses jsondata["Ad Scan"].groups
 * Widget auto-sizes to fit all content
 */
export const ADGroupsAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Group Name': 'showGroupName', 'Created': 'showCreated', 'Distinguished Name': 'showDistinguishedName', 'Security Group': 'showSecurityGroup', 'Managed BY': 'showManagedBy', 'Members': 'showMembers', 'Description': 'showDescription' });
  const {
    title = 'Audit Active Directory Groups',
    showNo = true,
    showGroupName = true,
    showCreated = true,
    showDistinguishedName = true,
    showSecurityGroup = true,
    showManagedBy = true,
    showMembers = true,
    showDescription = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-groups-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allGroups = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADGroupsAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayGroups = useMemo(() => {
    if (!pagination) return allGroups;
    const slice = getPaginationSlice(pagination);
    return allGroups.slice(slice.startIndex, slice.endIndex);
  }, [allGroups, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showGroupName ? '1.5fr' : '',
    showCreated ? '1fr' : '',
    showDistinguishedName ? '2fr' : '',
    showSecurityGroup ? '1fr' : '',
    showManagedBy ? '1.5fr' : '',
    showMembers ? '2fr' : '',
    showDescription ? '1.5fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '10px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    backgroundColor: '#f8f9fa'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="ad-groups-audit-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="ad-groups-audit-table" style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
          <div className="ad-groups-header" style={{ 
            display: 'grid', 
            gridTemplateColumns: templateColumns,
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            {showNo && <div style={headerStyle}>#</div>}
            {showGroupName && <div style={headerStyle}>Group Name</div>}
            {showCreated && <div style={headerStyle}>Created</div>}
            {showDistinguishedName && <div style={headerStyle}>Distinguished Name</div>}
            {showSecurityGroup && <div style={headerStyle}>Security</div>}
            {showManagedBy && <div style={headerStyle}>Managed By</div>}
            {showMembers && <div style={headerStyle}>Members</div>}
            {showDescription && <div style={headerStyle}>Description</div>}
          </div>

          <div className="ad-groups-body">
            {displayGroups.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#fff' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No AD groups found.</p>
              </div>
            ) : (
              displayGroups.map((group, idx) => (
                <div key={idx} className="ad-groups-row" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: templateColumns,
                  borderBottom: '1px solid #eee',
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                }}>
                  {showNo && <div style={rowStyleMerged}>{group.no}</div>}
                  {showGroupName && <div style={rowStyleMerged}>{group.group_name}</div>}
                  {showCreated && <div style={rowStyleMerged}>{group.created}</div>}
                  {showDistinguishedName && <div style={rowStyleMerged}><span style={{ fontSize: '0.85em', color: '#666' }}>{group.distinguished_name}</span></div>}
                  {showSecurityGroup && <div style={rowStyleMerged}>{group.security_group}</div>}
                  {showManagedBy && <div style={rowStyleMerged}>{group.managed_by}</div>}
                  {showMembers && (
                    <div style={rowStyleMerged}>
                      {group.members.length > 0 ? (
                        group.members.map((m, midx) => <div key={midx} style={{ fontSize: '0.85em' }}>• {m}</div>)
                      ) : '-'}
                    </div>
                  )}
                  {showDescription && <div style={rowStyleMerged}>{group.description}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD OUs audit data from scan results
 * Based on OU_list_table_format from template1.js:2688-2705
 */
export function extractADOUsAuditData(data, config = {}) {
  const ousData = [];
  const lookoutField = config.lookoutField || 'Members';

  if (!data) return ousData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const ous = adData.OU_list || [];

  if (!Array.isArray(ous) || ous.length === 0) {
    return ousData;
  }

  let count = 1;
  for (const ouItem of ous) {
    const ouName = ouItem.ou || "-";
    const dn = ouItem.distinguishedName || "-";
    const members = Array.isArray(ouItem.members) ? ouItem.members : (ouItem.members ? [ouItem.members.toString()] : []);

    if (lookoutField === 'Members' && members.length > 0) {
      // Per item/value rows for Members
      for (const member of members) {
        ousData.push({
          no: count++,
          ou: ouName,
          distinguished_name: dn,
          members: member || '-',
          all_members: members // Keep for full view if needed
        });
      }
    } else {
      // Default one row per OU
      ousData.push({
        no: count++,
        ou: ouName,
        distinguished_name: dn,
        members: members,
        all_members: members
      });
    }
  }

  return ousData;
}

/**
 * AD Organization Units Audit Table Widget
 * Based on OU_list_table_format from template1.js:2688-2705
 * Uses jsondata["Ad Scan"].OU_list
 * Widget auto-sizes to fit all content
 */
export const ADOUsAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const lookoutField = config.lookoutField || 'Members';
  
  // Apply hidden columns based on config overrides
  const defaultHidden = { 
    'No.': 'showNo', 
    'OU': 'showOU', 
    'Distinguished Name': 'showDistinguishedName', 
    'Members': 'showMembers' 
  };
    
  const hiddenOverrides = applyHiddenColumns(config, defaultHidden);

  const {
    title = 'Audit Organization Units',
    showNo = true,
    showOU = true,
    showDistinguishedName = true,
    showMembers = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-ous-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allOUs = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADOUsAuditData(data, config), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config, config.tableFilters, config.tableSort]);

  const displayOUs = useMemo(() => {
    if (!pagination) return allOUs;
    const slice = getPaginationSlice(pagination);
    return allOUs.slice(slice.startIndex, slice.endIndex);
  }, [allOUs, pagination]);

  // Determine column order based on lookoutField
  const columnOrder = ['No.'];
  const dataColumns = ['OU', 'Distinguished Name', 'Members'];
  
  const lookoutFieldName = lookoutField === 'Distinguished Name' ? 'Distinguished Name' : lookoutField;
  const filteredDataColumns = dataColumns.filter(c => c !== lookoutFieldName);
  columnOrder.push(lookoutFieldName, ...filteredDataColumns);

  const columnVisibility = {
    'No.': showNo,
    'OU': showOU,
    'Distinguished Name': showDistinguishedName,
    'Members': showMembers
  };

  const activeColumns = columnOrder.filter(c => columnVisibility[c]);
  const visibleColumnsCount = activeColumns.length;

  const templateColumns = visibleColumnsCount === 4
    ? '0.5fr 1.5fr 2fr 2fr'
    : Array(visibleColumnsCount).fill('1fr').join(' ');

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '10px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    backgroundColor: '#f8f9fa'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const renderCellValue = (ou, col) => {
    const val = (() => {
      switch (col) {
        case 'No.': return ou.no;
        case 'OU': return ou.ou;
        case 'Distinguished Name': return ou.distinguished_name;
        case 'Members': return ou.members;
        default: return '-';
      }
    })();

    if (col === 'Members' && Array.isArray(val)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {val.length > 0 ? val.map((m, i) => <div key={i} style={{ fontSize: '0.85em' }}>• {m}</div>) : '-'}
        </div>
      );
    }
    
    if (col === 'Distinguished Name') {
        return <span style={{ fontSize: '0.85em', color: '#666' }}>{val}</span>;
    }

    return val;
  };

  return (
    <div className="ad-ous-audit-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="ad-ous-audit-table" style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
          <div className="ad-ous-header" style={{ 
            display: 'grid', 
            gridTemplateColumns: templateColumns,
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            {activeColumns.map(col => (
              <div key={col} style={headerStyle}>{getDisplayHeader(config, col)}</div>
            ))}
          </div>

          <div className="ad-ous-body">
            {displayOUs.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#fff' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No organization units found.</p>
              </div>
            ) : (
              displayOUs.map((ou, idx) => (
                <div key={idx} className="ad-ous-row" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: templateColumns,
                  borderBottom: '1px solid #eee',
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                }}>
                  {activeColumns.map(col => (
                    <div key={col} style={rowStyleMerged}>{renderCellValue(ou, col)}</div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Domain Controllers audit data from scan results
 * Based on AD_table_format_DC from template1.js:2581-2602
 */
export function extractADDomainControllersAuditData(data) {
  const dcData = [];

  if (!data) return dcData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const domainControllers = adData.domain_controllers || [];

  if (!Array.isArray(domainControllers) || domainControllers.length === 0) {
    return dcData;
  }

  let count = 1;
  for (const dc of domainControllers) {
    dcData.push({
      no: count++,
      name: dc.name || '-',
      dns_name: dc.dns_name || '-',
      os_version: dc.os_version || '-',
      os: dc.os || '-',
      last_logon: dc.last_logon || '-',
      created: (dc.when_created || '').split('T')[0] || '-',
      last_password_set: dc.password_last_set || '-',
      disabled: dc.is_disabled === 1 ? 'Yes' : 'No',
      account_flags: dc.account_flags || '-'
    });
  }

  return dcData;
}

/**
 * AD Domain Controllers Audit Table Widget
 * Based on AD_table_format_DC from template1.js:2581-2602
 * Uses jsondata["Ad Scan"].domain_controllers
 * Widget auto-sizes to fit all content
 */
export const ADDomainControllersAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Name': 'showName', 'DNS Name': 'showDNSName', 'OS Version': 'showOSVersion', 'OS': 'showOS', 'Last Logon': 'showLastLogon', 'Created': 'showCreated', 'Last Password Set': 'showLastPasswordSet', 'Disabled': 'showDisabled', 'Account Flags': 'showAccountFlags' });
  const {
    title = 'Audit Domain Controllers',
    showNo = true,
    showName = true,
    showDNSName = true,
    showOSVersion = true,
    showOS = true,
    showLastLogon = true,
    showCreated = true,
    showLastPasswordSet = true,
    showDisabled = true,
    showAccountFlags = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-domain-controllers-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allDCs = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADDomainControllersAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayDCs = useMemo(() => {
    if (!pagination) return allDCs;
    const slice = getPaginationSlice(pagination);
    return allDCs.slice(slice.startIndex, slice.endIndex);
  }, [allDCs, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showName ? '1.5fr' : '',
    showDNSName ? '1.5fr' : '',
    showOSVersion ? '1fr' : '',
    showOS ? '1fr' : '',
    showLastLogon ? '1.5fr' : '',
    showCreated ? '1fr' : '',
    showLastPasswordSet ? '1.5fr' : '',
    showDisabled ? '1fr' : '',
    showAccountFlags ? '1.5fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDNSName && <div style={headerStyle}>{getDisplayHeader(config, 'DNS Name')}</div>}
            {showOSVersion && <div style={headerStyle}>{getDisplayHeader(config, 'OS Version')}</div>}
            {showOS && <div style={headerStyle}>{getDisplayHeader(config, 'OS')}</div>}
            {showLastLogon && <div style={headerStyle}>{getDisplayHeader(config, 'Last Logon')}</div>}
            {showCreated && <div style={headerStyle}>{getDisplayHeader(config, 'Created')}</div>}
            {showLastPasswordSet && <div style={headerStyle}>{getDisplayHeader(config, 'Last Password Set')}</div>}
            {showDisabled && <div style={headerStyle}>{getDisplayHeader(config, 'Disabled')}</div>}
            {showAccountFlags && <div style={headerStyle}>{getDisplayHeader(config, 'Account Flags')}</div>}
          </div>

          <div className="open-ports-body">
            {displayDCs.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No domain controllers found.</p>
              </div>
            ) : (
              displayDCs.map((dc, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showName && <div style={rowStyleMerged}>{dc.name}</div>}
                    {showDNSName && <div style={rowStyleMerged}>{dc.dns_name}</div>}
                    {showOSVersion && <div style={rowStyleMerged}>{dc.os_version}</div>}
                    {showOS && <div style={rowStyleMerged}>{dc.os}</div>}
                    {showLastLogon && <div style={rowStyleMerged}>{dc.last_logon}</div>}
                    {showCreated && <div style={rowStyleMerged}>{dc.created}</div>}
                    {showLastPasswordSet && <div style={rowStyleMerged}>{dc.last_password_set}</div>}
                    {showDisabled && <div style={rowStyleMerged}>{dc.disabled}</div>}
                    {showAccountFlags && <div style={rowStyleMerged}>{dc.account_flags}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD GPO Policies audit data from scan results
 * Based on ad_gpoPolicies_table_format from template1.js:2740-2768
 */
export function extractADGPOPoliciesAuditData(data) {
  const policiesData = [];

  if (!data) return policiesData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const policies = adData?.gpo_policies || [];

  if (!Array.isArray(policies) || policies.length === 0) {
    return policiesData;
  }

  const formatExtensions = (extList) => {
    if (!Array.isArray(extList) || extList.length === 0) return "-";
    return extList
      .map(ext => `${ext.description} (${ext.guid})`)
      .join("; ");
  };

  let count = 1;
  for (const policy of policies) {
    policiesData.push({
      no: count++,
      guid: policy.guid || "-",
      policy_name: policy.name || "-",
      machine_extensions: formatExtensions(policy.machine_extensions),
      user_extensions: formatExtensions(policy.user_extensions)
    });
  }

  return policiesData;
}

/**
 * AD GPO Policies Audit Table Widget
 * Based on ad_gpoPolicies_table_format from template1.js:2740-2768
 * Uses jsondata["Ad Scan"]?.gpo_policies
 * Widget auto-sizes to fit all content
 */
export const ADGPOPoliciesAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'GUID': 'showGUID', 'Policy Name': 'showPolicyName', 'Machine Extensions': 'showMachineExtensions', 'User Extensions': 'showUserExtensions' });
  const {
    title = 'Audit GPO Policies',
    showNo = true,
    showGUID = true,
    showPolicyName = true,
    showMachineExtensions = true,
    showUserExtensions = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-gpo-policies-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPolicies = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADGPOPoliciesAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayPolicies = useMemo(() => {
    if (!pagination) return allPolicies;
    const slice = getPaginationSlice(pagination);
    return allPolicies.slice(slice.startIndex, slice.endIndex);
  }, [allPolicies, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showGUID ? '2fr' : '',
    showPolicyName ? '1.5fr' : '',
    showMachineExtensions ? '2fr' : '',
    showUserExtensions ? '2fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showGUID && <div style={headerStyle}>{getDisplayHeader(config, 'GUID')}</div>}
            {showPolicyName && <div style={headerStyle}>{getDisplayHeader(config, 'Policy Name')}</div>}
            {showMachineExtensions && <div style={headerStyle}>{getDisplayHeader(config, 'Machine Extensions')}</div>}
            {showUserExtensions && <div style={headerStyle}>{getDisplayHeader(config, 'User Extensions')}</div>}
          </div>

          <div className="open-ports-body">
            {displayPolicies.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No GPO policies found.</p>
              </div>
            ) : (
              displayPolicies.map((policy, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showGUID && <div style={rowStyleMerged}>{policy.guid}</div>}
                    {showPolicyName && <div style={rowStyleMerged}>{policy.policy_name}</div>}
                    {showMachineExtensions && <div style={rowStyleMerged}>{policy.machine_extensions}</div>}
                    {showUserExtensions && <div style={rowStyleMerged}>{policy.user_extensions}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

// Sanitize text helper
const sanitizeText = (text) => {
  return String(text)
    .replace(/[\r\n]+/g, ' ')
    .replace(/[^\x20-\x7E]+/g, '')
    .trim();
};

// Truncate text helper
const truncateText = (text, maxLength = 500) => {
  const safeText = String(text || '').replace(/[\r\n]+/g, ' ').replace(/[^\x20-\x7E]+/g, '');
  return safeText.length > maxLength ? safeText.slice(0, maxLength) + '...' : safeText;
};

/**
 * Extract AD Event Logs audit data from scan results
 * Based on ad_events_table_format from template1.js:2769-2790
 */
export function extractADEventLogsAuditData(data) {
  const eventLogsData = [];

  if (!data) return eventLogsData;

  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const event_log = adData.event_log || [];

  if (!Array.isArray(event_log) || event_log.length === 0) {
    return eventLogsData;
  }

  let count = 1;
  for (const event of event_log) {
    const timestamp = event.timestamp ? event.timestamp.replace('T', ' ') : '-';
    const event_id = event.event_id || '-';
    const event_type = event.event_type || '-';
    const source = event.source || '-';
    const category = event.category || '-';
    const record_number = event.record_number || '-';
    const message = event.message || '-';

    eventLogsData.push({
      no: count++,
      timestamp: timestamp,
      event_id: event_id,
      event_type: event_type,
      source: sanitizeText(source),
      category: sanitizeText(category),
      record_number: record_number,
      message: truncateText(message)
    });
  }

  return eventLogsData;
}

/**
 * AD Event Logs Audit Table Widget
 * Based on ad_events_table_format from template1.js:2769-2790
 * Uses jsondata["Ad Scan"].event_log
 * Widget auto-sizes to fit all content
 */
export const ADEventLogsAuditTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { '#': 'showNo', 'Timestamp': 'showTimestamp', 'Event ID': 'showEventID', 'Type': 'showEventType', 'Source': 'showSource', 'Category': 'showCategory', 'Record #': 'showRecordNumber', 'Message': 'showMessage' });
  const {
    title = 'Audit Event Logs',
    showNo = true,
    showTimestamp = true,
    showEventID = true,
    showEventType = true,
    showSource = true,
    showCategory = true,
    showRecordNumber = true,
    showMessage = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-event-logs-audit-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allEvents = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADEventLogsAuditData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayEvents = useMemo(() => {
    if (!pagination) return allEvents;
    const slice = getPaginationSlice(pagination);
    return allEvents.slice(slice.startIndex, slice.endIndex);
  }, [allEvents, pagination]);

  const templateColumns = [
    showNo ? '0.5fr' : '',
    showTimestamp ? '1.5fr' : '',
    showEventID ? '1fr' : '',
    showEventType ? '1fr' : '',
    showSource ? '1.5fr' : '',
    showCategory ? '1.5fr' : '',
    showRecordNumber ? '1fr' : '',
    showMessage ? '3fr' : ''
  ].filter(Boolean).join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, '#')}</div>}
            {showTimestamp && <div style={headerStyle}>{getDisplayHeader(config, 'Timestamp')}</div>}
            {showEventID && <div style={headerStyle}>{getDisplayHeader(config, 'Event ID')}</div>}
            {showEventType && <div style={headerStyle}>{getDisplayHeader(config, 'Type')}</div>}
            {showSource && <div style={headerStyle}>{getDisplayHeader(config, 'Source')}</div>}
            {showCategory && <div style={headerStyle}>{getDisplayHeader(config, 'Category')}</div>}
            {showRecordNumber && <div style={headerStyle}>{getDisplayHeader(config, 'Record #')}</div>}
            {showMessage && <div style={headerStyle}>{getDisplayHeader(config, 'Message')}</div>}
          </div>

          <div className="open-ports-body">
            {displayEvents.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No event logs found.</p>
              </div>
            ) : (
              displayEvents.map((event, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showTimestamp && <div style={rowStyleMerged}>{event.timestamp}</div>}
                    {showEventID && <div style={rowStyleMerged}>{event.event_id}</div>}
                    {showEventType && <div style={rowStyleMerged}>{event.event_type}</div>}
                    {showSource && <div style={rowStyleMerged}>{event.source}</div>}
                    {showCategory && <div style={rowStyleMerged}>{event.category}</div>}
                    {showRecordNumber && <div style={rowStyleMerged}>{event.record_number}</div>}
                    {showMessage && <div style={rowStyleMerged}>{event.message}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract Network Logical Disks Data
 * Flattens all logical disks from all network devices
 */
export const extractNetworkLogicalDisksData = (data) => {
  try {
    const scanDataSource = data?.scanData || data?.data || data;
    const devices = scanDataSource?.network_devices || [];
    const allDisks = [];

    devices.forEach((device) => {
      const logicalDisks = device?.system?.['Logical Disks'] || [];
      if (Array.isArray(logicalDisks)) {
        logicalDisks.forEach((disk) => {
          const size = parseFloat(disk['Size (GB)'] || disk.size || 0);
          const used = parseFloat(disk['Used (GB)'] || disk.used || 0);
          const free = parseFloat(disk['Free (GB)'] || disk.free || 0);
          
          let percentUsed = disk['Percent Used'] || disk.percent_used;
          if (!percentUsed && size > 0) {
            percentUsed = `${((used / size) * 100).toFixed(2)}%`;
          }

          allDisks.push({
            device_info: device.hostname || device.ip || 'Unknown',
            computer_name: disk['Computer Name'] || device.hostname || device.ip || 'Unknown',
            drive_name: disk['Drive Name'] || disk.drive || '',
            file_system: disk['File System'] || disk.filesystem || '',
            size_gb: size.toFixed(2),
            used_gb: used.toFixed(2),
            free_gb: free.toFixed(2),
            percent_used: percentUsed || '0%'
          });
        });
      }
    });

    return allDisks;
  } catch (error) {
    console.error('Error extractNetworkLogicalDisksData', error);
    return [];
  }
};

/**
 * Network - Logical Disks Table
 */
export const NetworkLogicalDisksTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 
    'No.': 'showNo', 
    'Device': 'showDevice',
    'Computer Name': 'showComputerName', 
    'Drive Name': 'showDriveName', 
    'File System': 'showFileSystem', 
    'Size (GB)': 'showSizeGB', 
    'Used (GB)': 'showUsedGB', 
    'Free (GB)': 'showFreeGB', 
    'Disk Usage %': 'showDiskUsagePercent' 
  });
  const {
    title = 'Network Logical Disks',
    showNo = true,
    showDevice = true,
    showComputerName = true,
    showDriveName = true,
    showFileSystem = true,
    showSizeGB = true,
    showUsedGB = true,
    showFreeGB = true,
    showDiskUsagePercent = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-logical-disks-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allDiskData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractNetworkLogicalDisksData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayDiskRows = useMemo(() => {
    if (!pagination) return allDiskData;
    const slice = getPaginationSlice(pagination);
    return allDiskData.slice(slice.startIndex, slice.endIndex);
  }, [allDiskData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumnsCount = [
    showNo, showDevice, showComputerName, showDriveName, showFileSystem, 
    showSizeGB, showUsedGB, showFreeGB, showDiskUsagePercent
  ].filter(Boolean).length;
  
  const templateColumns = visibleColumnsCount === 9
    ? '0.5fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr'
    : Array(visibleColumnsCount).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showComputerName && <div style={headerStyle}>{getDisplayHeader(config, 'Computer Name')}</div>}
            {showDriveName && <div style={headerStyle}>{getDisplayHeader(config, 'Drive Name')}</div>}
            {showFileSystem && <div style={headerStyle}>{getDisplayHeader(config, 'File System')}</div>}
            {showSizeGB && <div style={headerStyle}>{getDisplayHeader(config, 'Size (GB)')}</div>}
            {showUsedGB && <div style={headerStyle}>{getDisplayHeader(config, 'Used (GB)')}</div>}
            {showFreeGB && <div style={headerStyle}>{getDisplayHeader(config, 'Free (GB)')}</div>}
            {showDiskUsagePercent && <div style={headerStyle}>{getDisplayHeader(config, 'Disk Usage %')}</div>}
          </div>
          <div className="open-ports-body">
            {displayDiskRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No logical disk data available.</p>
              </div>
            ) : (
              displayDiskRows.map((disk, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showDevice && <div style={rowStyleMerged}>{disk.device_info}</div>}
                    {showComputerName && <div style={rowStyleMerged}>{disk.computer_name}</div>}
                    {showDriveName && <div style={rowStyleMerged}>{disk.drive_name}</div>}
                    {showFileSystem && <div style={rowStyleMerged}>{disk.file_system}</div>}
                    {showSizeGB && <div style={rowStyleMerged}>{disk.size_gb}</div>}
                    {showUsedGB && <div style={rowStyleMerged}>{disk.used_gb}</div>}
                    {showFreeGB && <div style={rowStyleMerged}>{disk.free_gb}</div>}
                    {showDiskUsagePercent && <div style={rowStyleMerged}>{disk.percent_used}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extractor for Workstation Age Table
 */
export const extractADWorkstationAgeData = (data, config = {}) => {
  const category_thresholds = data?.category_thresholds || {};
  let thresholds = category_thresholds?.['Workstation Age'];
  if (!thresholds) {
      const match = Object.entries(category_thresholds).find(([k]) => k.trim().toLowerCase() === 'workstation age');
      if (match) thresholds = match[1];
  }
  const warningLowerBound = thresholds?.rules?.Moderate?.lower || 25;
  const criticalLowerBound = thresholds?.rules?.Bad?.value || 36;

  const scanDataSource = data?.scanData || data?.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const computers = adData?.Computers || [];

  const rawMapped = computers.map((computer, index) => {
    let ageMonths = null;
    let ageDisplay = 'Unknown';
    let addedToDomainDisplay = 'Unknown';
    let status = 'Unknown';

    const createdValue = computer.whenCreated || computer.created;
    if (createdValue) {
      try {
        let dateStr = createdValue;
        let createdDate;

        if (typeof dateStr === 'string' && dateStr.includes('.0Z')) {
          const year = parseInt(dateStr.substring(0, 4));
          const month = parseInt(dateStr.substring(4, 6)) - 1;
          const day = parseInt(dateStr.substring(6, 8));
          const hour = parseInt(dateStr.substring(8, 10));
          const minute = parseInt(dateStr.substring(10, 12));
          const second = parseInt(dateStr.substring(12, 14));
          createdDate = new Date(Date.UTC(year, month, day, hour, minute, second));
        } else {
          createdDate = new Date(dateStr);
        }

        if (!isNaN(createdDate.getTime())) {
          const now = new Date();
          const diffTime = Math.abs(now - createdDate);
          ageMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.4375));
          ageDisplay = `${ageMonths} months`;

          if (ageMonths > criticalLowerBound) {
            status = 'Critical';
          } else if (ageMonths >= warningLowerBound && ageMonths <= criticalLowerBound) {
            status = 'Warning';
          } else {
            status = 'Healthy';
          }

          const pad = (n) => n.toString().padStart(2, '0');
          const mm = pad(createdDate.getMonth() + 1);
          const dd = pad(createdDate.getDate());
          const yyyy = createdDate.getFullYear();
          const hh = pad(createdDate.getHours());
          const min = pad(createdDate.getMinutes());
          addedToDomainDisplay = `${mm}-${dd}-${yyyy} ${hh}:${min} CST`;
        }
      } catch (e) {
        console.warn('Error parsing date:', createdValue);
        addedToDomainDisplay = typeof createdValue === 'string' && createdValue.includes('T') ? createdValue.split('T')[0] : createdValue;
      }
    } else {
      addedToDomainDisplay = 'Unknown';
    }

    const lastLogon = computer.LastLogonDate || computer.lastLogon || computer.last_logon || 'Unknown';

    return {
      no: index + 1,
      hostname: computer.Name || computer.ComputerName || computer.name || computer.host || 'Unknown',
      lastLogon: typeof lastLogon === 'string' && lastLogon.includes('T') ? lastLogon.split('T')[0] : lastLogon,
      addedToDomain: addedToDomainDisplay,
      ageMonthsNum: ageMonths !== null ? ageMonths : -1,
      ageMonths: ageDisplay,
      status: status
    };
  });

  let filteredResults = rawMapped;
  const ageThreshold = parseInt(config.ageThreshold, 10);
  if (!isNaN(ageThreshold) && ageThreshold > 0) {
      filteredResults = filteredResults.filter(ws => ws.ageMonthsNum >= ageThreshold);
  }

  return filteredResults.map((ws, i) => ({ ...ws, no: i + 1 }));
};

/**
 * AD Workstation Age Table Widget
 */
export const ADWorkstationAgeTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Hostname': 'showHostname', 'Last Logon': 'showLastLogon', 'Added to Domain': 'showAddedToDomain', 'Workstation Age': 'showAge' });
  
  const {
    title = 'Workstation Age',
    showNo = true,
    showHostname = true,
    showLastLogon = true,
    showAddedToDomain = true,
    showAge = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-workstation-age-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allDiskData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADWorkstationAgeData(data, config), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort, config]);

  const displayRows = useMemo(() => {
    if (!pagination) return allDiskData;
    const slice = getPaginationSlice(pagination);
    return allDiskData.slice(slice.startIndex, slice.endIndex);
  }, [allDiskData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumnsCount = [
    showNo, showHostname, showLastLogon, showAddedToDomain, showAge
  ].filter(Boolean).length;
  
  const templateColumns = visibleColumnsCount === 5
    ? '0.5fr 1.5fr 1fr 1fr 1fr'
    : Array(visibleColumnsCount).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showLastLogon && <div style={headerStyle}>{getDisplayHeader(config, 'Last Logon')}</div>}
            {showAddedToDomain && <div style={headerStyle}>{getDisplayHeader(config, 'Added to Domain')}</div>}
            {showAge && <div style={headerStyle}>{getDisplayHeader(config, 'Workstation Age (Months)')}</div>}
          </div>
          <div className="open-ports-body">
            {displayRows.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No workstations exceed selected age threshold.</p>
              </div>
            ) : (
              displayRows.map((ws, idx) => {
                const startIndex = pagination ? getPaginationSlice(pagination).startIndex : 0;
                const actualNumber = startIndex + idx + 1;
                
                const statusColors = {
                  'Critical': theme?.statusColors?.red || '#d71920',
                  'Warning': theme?.statusColors?.yellow || '#fdbc13',
                  'Healthy': theme?.statusColors?.green || '#6fca42',
                  'Unknown': '#666'
                };
                const statusColor = statusColors[ws.status] || '#666';

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div style={rowStyleMerged}>{actualNumber}</div>}
                    {showHostname && <div style={rowStyleMerged}>{ws.hostname}</div>}
                    {showLastLogon && <div style={rowStyleMerged}>{ws.lastLogon}</div>}
                    {showAddedToDomain && <div style={rowStyleMerged}>{ws.addedToDomain}</div>}
                    {showAge && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                            {ws.ageMonths}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

// Export all widgets as named exports (not default)
// This allows them to be imported individually in AllProofTablesWidgets.jsx
